import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useDealer } from '../context/DealerContext';
import { useLanguage } from '../context/LanguageContext';
import { LogOut, Package, Calculator, Palette, FileText, Building2, Loader2, MapPin, Bookmark, ScrollText, ExternalLink, BarChart3, ShoppingCart, Users, UserCog, ChevronDown, ChevronRight, Download, Plus, Minus, Trash2, Check, Phone, Mail, Bell, X, ClipboardList, ListTodo, Search, Eye, Gamepad2, BookOpen, Bot, Instagram, MessageCircle, Tag, Image as ImageIcon } from 'lucide-react';
import PanelCalculator from '../components/PanelCalculator';
import InteriorConstructor from '../components/InteriorConstructor';
import MyVisualizationsGallery from '../components/MyVisualizationsGallery';
import AIStatsTab from '../components/AIStatsTab';
import StaffTab from '../components/StaffTab';
import ChangeMyPasswordButton from '../components/ChangeMyPasswordButton';
import ObjectsMap from '../components/ObjectsMap';
import CRMPanel from '../components/CRMPanel';
import TaskBoard from '../components/TaskBoard';
import { NewBadge, isNew } from '../components/NewBadge';
import { API_URL } from '../config';

const texts = {
  ru: {
    welcome: 'Добро пожаловать',
    tabs: { crm: 'CRM', tasks: 'Задачник', objects: 'Объекты', myObjects: 'Мои объекты', catalog: 'Каталог', stock: 'Остатки', orders: 'Заказы дилеров', calculator: 'Калькулятор', constructor: 'Визуализация', gallery: 'Мои визуализации', prices: 'Прайсы', contracts: 'Договор', docs: 'Документы', script: 'Скрипт продаж', trainer: 'Тренажёр', ai_stats: 'AI-статистика', staff: 'Сотрудники' },
    logout: 'Выйти', goToSite: 'На сайт',
    company: 'Компания',
    catalogTitle: 'Каталог продукции',
    catalogSubtitle: 'Актуальный каталог с дилерскими ценами',
    product: 'Товар', price: 'Цена', unit: 'Ед.', stock: 'Наличие',
    inStock: 'В наличии', outOfStock: 'Под заказ',
    priceNotSet: 'По запросу',
    docsTitle: 'Документация',
    docsSubtitle: 'Сертификаты и техническая документация',
    pricesTitle: 'Прайс-листы',
    pricesSubtitle: 'Актуальные дилерские прайс-листы на продукцию',
    contractsTitle: 'Договор + Спецификации',
    contractsSubtitle: 'Шаблоны договоров и спецификации для работы с клиентами',
    stockTitle: 'Остатки на складе',
    stockSubtitle: 'Данные из МойСклад в реальном времени',
    stockName: 'Товар', stockPrice: 'Цена', stockUnit: 'Ед.', stockQty: 'На складе', stockReserve: 'Резерв', stockAvail: 'Доступно',
    showPrice: 'Показать цены', hidePrice: 'Скрыть цены',
    saleBtn: 'Распродажа',
    saleBadge: 'СКИДКА 10%',
    ordersTitle: 'Заказы',
    ordersSubtitle: 'История заказов из МойСклад',
    orderNum: 'Номер', orderDate: 'Дата', orderSum: 'Сумма', orderStatus: 'Статус', orderClient: 'Клиент',
    noOrders: 'Заказов пока нет',
    panels: 'Панели', profiles: 'Профили', accessories: 'Комплектующие',
    download: 'Скачать', updated: 'Обновлено',
  },
  kz: {
    welcome: 'Қош келдіңіз',
    tabs: { crm: 'CRM', tasks: 'Тапсырмалар', objects: 'Нысандар', myObjects: 'Менің нысандарым', catalog: 'Каталог', stock: 'Қалдықтар', orders: 'Дилерлер тапсырыстары', calculator: 'Калькулятор', constructor: 'Визуализация', gallery: 'Менің визуализациям', prices: 'Бағалар', contracts: 'Шарт', docs: 'Құжаттар', script: 'Сату скрипті', trainer: 'Тренажёр', ai_stats: 'AI-статистика', staff: 'Қызметкерлер' },
    logout: 'Шығу', goToSite: 'Сайтқа',
    company: 'Компания',
    catalogTitle: 'Өнім каталогы',
    catalogSubtitle: 'Дилер бағаларымен каталог',
    product: 'Тауар', price: 'Баға', unit: 'Бір.', stock: 'Қолжетімділік',
    inStock: 'Қоймада', outOfStock: 'Тапсырыспен',
    priceNotSet: 'Сұрау бойынша',
    stockTitle: 'Қоймадағы қалдықтар',
    stockSubtitle: 'МойСклад деректері нақты уақытта',
    stockName: 'Тауар', stockPrice: 'Баға', stockUnit: 'Бір.', stockQty: 'Қоймада', stockReserve: 'Резерв', stockAvail: 'Қолжетімді',
    showPrice: 'Бағаларды көрсету', hidePrice: 'Бағаларды жасыру',
    saleBtn: 'Жарнама',
    saleBadge: 'ЖЕҢІЛДІК 10%',
    docsTitle: 'Құжаттама',
    docsSubtitle: 'Сертификаттар және техникалық құжаттар',
    pricesTitle: 'Прайс-парақтар',
    pricesSubtitle: 'Дилер бағалары',
    contractsTitle: 'Шарт + Спецификациялар',
    contractsSubtitle: 'Шарт үлгілері мен спецификациялар',
    panels: 'Панельдер', profiles: 'Профильдер', accessories: 'Жабдықтар',
    download: 'Жүктеу', updated: 'Жаңартылды',
  },
  en: {
    welcome: 'Welcome',
    tabs: { crm: 'CRM', tasks: 'Tasks', objects: 'Objects', myObjects: 'My Objects', catalog: 'Catalog', stock: 'Stock', orders: 'Dealer Orders', calculator: 'Calculator', constructor: 'Visualization', gallery: 'My Visualizations', prices: 'Prices', contracts: 'Contracts', docs: 'Documents', script: 'Sales Script', trainer: 'Trainer', ai_stats: 'AI Stats', staff: 'Staff' },
    logout: 'Log Out', goToSite: 'Website',
    company: 'Company',
    catalogTitle: 'Product Catalog',
    catalogSubtitle: 'Current catalog with dealer prices',
    product: 'Product', price: 'Price', unit: 'Unit', stock: 'Availability',
    inStock: 'In stock', outOfStock: 'On order',
    priceNotSet: 'On request',
    docsTitle: 'Documentation',
    docsSubtitle: 'Certificates and technical documents',
    stockTitle: 'Warehouse Stock',
    stockSubtitle: 'Real-time data from MoySklad',
    stockName: 'Product', stockPrice: 'Price', stockUnit: 'Unit', stockQty: 'In Stock', stockReserve: 'Reserved', stockAvail: 'Available',
    showPrice: 'Show prices', hidePrice: 'Hide prices',
    saleBtn: 'Sale',
    saleBadge: 'SALE 10%',
    pricesTitle: 'Price Lists',
    pricesSubtitle: 'Current dealer price lists',
    contractsTitle: 'Contracts + Specifications',
    contractsSubtitle: 'Contract templates and specifications for client work',
    panels: 'Panels', profiles: 'Profiles', accessories: 'Accessories',
    download: 'Download', updated: 'Updated',
  },
};



const categoryNames = { panels: 'panels', profiles: 'profiles', accessories: 'accessories' };

const isLightColor = (color) => {
  const hex = color.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 > 128;
};

const textureCollections = {
  oriental: {
    name: { ru: 'Восточные вкусы', kz: 'Шығыс дәмі', en: 'Oriental Flavour' },
    textures: [
      { name: { ru: 'Солёный курт', kz: 'Тұзды құрт', en: 'Salty Kurt' }, code: 'EAST-01-SK', color: '#f1ece1' },
      { name: { ru: 'Сгущённое молоко', kz: 'Қоймалжыңдалған сүт', en: 'Condensed Milk' }, code: 'EAST-02-SM', color: '#e6e1cd' },
      { name: { ru: 'Миндальный крем', kz: 'Бадам кремі', en: 'Almond Cream' }, code: 'EAST-04-MK', color: '#e5e2d8' },
      { name: { ru: 'Бархатная халва', kz: 'Барқыт халуа', en: 'Velvet Halva' }, code: 'EAST-05-BH', color: '#b6b0a2' },
      { name: { ru: 'Чай с молоком', kz: 'Сүтті шай', en: 'Tea with Milk' }, code: 'EAST-06-CH', color: '#cdb9a6' },
      { name: { ru: 'Кофе с молоком', kz: 'Сүтті кофе', en: 'Coffee with Milk' }, code: 'EAST-07-KM', color: '#af9e93' },
      { name: { ru: 'Пряная куркума', kz: 'Дәмді сарымсақ', en: 'Spicy Turmeric' }, code: 'EAST-08-PK', color: '#faaf49' },
      { name: { ru: 'Ароматный шафран', kz: 'Хош иісті загфиран', en: 'Aromatic Saffron' }, code: 'EAST-09-AS', color: '#e57c49' },
      { name: { ru: 'Нежная голубика', kz: 'Нәзік көк жидек', en: 'Tender Blueberry' }, code: 'EAST-10-NG', color: '#8599b4' },
      { name: { ru: 'Фисташковая паста', kz: 'Пісте пастасы', en: 'Pistachio Paste' }, code: 'EAST-11-FP', color: '#b2ab68' },
      { name: { ru: 'Лазурный зефир', kz: 'Көгілдір зефир', en: 'Azure Zephyr' }, code: 'EAST-12-LZ', color: '#cddfe1' },
      { name: { ru: 'Клубничный зефир', kz: 'Құлпынай зефирі', en: 'Strawberry Zephyr' }, code: 'EAST-13-KZ', color: '#eed1d3' },
      { name: { ru: 'Свежая черника', kz: 'Жаңа қара жидек', en: 'Fresh Blueberry' }, code: 'EAST-14-SC', color: '#3d4551' },
      { name: { ru: 'Спелый гранат', kz: 'Піскен анар', en: 'Ripe Pomegranate' }, code: 'EAST-15-SG', color: '#65272f' },
      { name: { ru: 'Душистая зелень', kz: 'Хош иісті жасылдық', en: 'Fragrant Greens' }, code: 'EAST-16-DZ', color: '#2d4434' },
    ],
  },
  grey: {
    name: { ru: '5 оттенков серого', kz: 'Сұрдың 5 реңкі', en: '5 Shades of Grey' },
    textures: [
      { name: { ru: 'Беж грей', kz: 'Беж сұр', en: 'Beige Grey' }, code: 'GRAY-01-BG', color: '#b2aea5' },
      { name: { ru: 'Лайт грей', kz: 'Ашық сұр', en: 'Light Grey' }, code: 'GRAY-02-LG', color: '#c8c8c7' },
      { name: { ru: 'Тотал грей', kz: 'Толық сұр', en: 'Total Grey' }, code: 'GRAY-03-TG', color: '#a4a8a9' },
      { name: { ru: 'Дарк грей', kz: 'Қою сұр', en: 'Dark Grey' }, code: 'GRAY-04-DG', color: '#747774' },
      { name: { ru: 'Графит грей', kz: 'Графит сұр', en: 'Graphite Grey' }, code: 'GRAY-05-GG', color: '#55585c' },
    ],
  },
  nomad: {
    name: { ru: 'Номад', kz: 'Номад', en: 'Nomad' },
    ornaments: [
      { name: { ru: 'Алтын Мозаика I', kz: 'Алтын Мозаика I', en: 'Golden Mosaic I' }, code: 'NMD-AM1' },
      { name: { ru: 'Алтын Мозаика II', kz: 'Алтын Мозаика II', en: 'Golden Mosaic II' }, code: 'NMD-AM2' },
      { name: { ru: 'Бурханский кирпич', kz: 'Бұрқан кірпіш', en: 'Burkhan Brick' }, code: 'NMD-BRK' },
      { name: { ru: 'Дала Алқабы', kz: 'Дала Алқабы', en: 'Dala Alcove' }, code: 'NMD-DA' },
      { name: { ru: 'Дәстүрлі сандық I', kz: 'Дәстүрлі сандық I', en: 'Traditional Chest I' }, code: 'NMD-DS1' },
      { name: { ru: 'Дәстүрлі сандық II', kz: 'Дәстүрлі сандық II', en: 'Traditional Chest II' }, code: 'NMD-DS2' },
      { name: { ru: 'Күмістей Маржан', kz: 'Күмістей Маржан', en: 'Silver Pearl' }, code: 'NMD-KM' },
      { name: { ru: 'Көшпелі пайыз', kz: 'Көшпелі пайыз', en: 'Nomadic Pattern' }, code: 'NMD-KP' },
      { name: { ru: 'Орнамент I', kz: 'Орнамент I', en: 'Ornament I' }, code: 'NMD-OR1' },
      { name: { ru: 'Орнамент II', kz: 'Орнамент II', en: 'Ornament II' }, code: 'NMD-OR2' },
      { name: { ru: 'Орнамент III', kz: 'Орнамент III', en: 'Ornament III' }, code: 'NMD-OR3' },
      { name: { ru: 'Ордабасы', kz: 'Ордабасы', en: 'Ordabasy' }, code: 'NMD-ORD' },
      { name: { ru: 'Витраж', kz: 'Витраж', en: 'Stained Glass' }, code: 'NMD-VTR' },
    ],
    colors: [
      { name: { ru: 'Бархатная халва', kz: 'Барқыт халуа', en: 'Velvet Halva' }, code: 'BH', color: '#b6b0a2' },
      { name: { ru: 'Чай с молоком', kz: 'Сүтті шай', en: 'Tea with Milk' }, code: 'CH', color: '#cdb9a6' },
      { name: { ru: 'Душистая зелень', kz: 'Хош иісті жасылдық', en: 'Fragrant Greens' }, code: 'DZ', color: '#2d4434' },
      { name: { ru: 'Нежная голубика', kz: 'Нәзік көк жидек', en: 'Tender Blueberry' }, code: 'NG', color: '#8599b4' },
      { name: { ru: 'Свежая черника', kz: 'Жаңа қара жидек', en: 'Fresh Blueberry' }, code: 'SC', color: '#3d4551' },
      { name: { ru: 'Спелый гранат', kz: 'Піскен анар', en: 'Ripe Pomegranate' }, code: 'SG', color: '#65272f' },
      { name: { ru: 'Золотой сандық', kz: 'Алтын сандық', en: 'Golden Chest' }, code: 'ZS', color: '#c9a65c' },
    ],
  },
  standard: {
    name: { ru: 'Стандарт', kz: 'Стандарт', en: 'Standard' },
    textures: [
      { name: { ru: 'Бетон Белый', kz: 'Бетон Ақ', en: 'Concrete White' }, code: '105021', color: '#f5f5f5' },
      { name: { ru: 'Бетон Светло Серый', kz: 'Бетон Ашық Сұр', en: 'Concrete Light Grey' }, code: '105002', color: '#d4d4d4' },
      { name: { ru: 'Бетон Темно Серый', kz: 'Бетон Қою Сұр', en: 'Concrete Dark Grey' }, code: '105001', color: '#6b6b6b' },
      { name: { ru: 'Лён Натуральный', kz: 'Табиғи Зығыр', en: 'Natural Linen' }, code: 'FS119007-134', color: '#d8d3c5', image: '/images/catalog/standard_FS119007-134.png', newUntil: '2026-12-14' },
      { name: { ru: 'Дуб Натуральный', kz: 'Табиғи Емен', en: 'Natural Oak' }, code: 'FK16166-988', color: '#b09a7b', image: '/images/catalog/standard_FK16166-988.png', newUntil: '2026-12-14' },
      { name: { ru: 'Дуб Шоколадный', kz: 'Шоколадты Емен', en: 'Chocolate Oak' }, code: 'FK16167-988', color: '#6b5a44', image: '/images/catalog/standard_FK16167-988.png', newUntil: '2026-12-14' },
    ],
  },
  premium: {
    name: { ru: 'Премиум', kz: 'Премиум', en: 'Premium' },
    textures: [
      { name: { ru: 'Штукатурка Капучино', kz: 'Сылақ Капучино', en: 'Plaster Cappuccino' }, code: 'LS 1923-2-L', color: '#c9b8a5' },
      { name: { ru: 'Штукатурка Лайт Грей', kz: 'Сылақ Ашық Сұр', en: 'Plaster Light Grey' }, code: 'LS 1922-2-L', color: '#c8c8c8' },
      { name: { ru: 'Шпон Орех Натуральный Софт', kz: 'Жаңғақ Табиғи Жұмсақ', en: 'Natural Walnut Veneer Soft' }, code: 'ZB 254-SFT', color: '#6b5344' },
      { name: { ru: 'Дуб Бомонт Натуральный', kz: 'Бомонт Еменi Табиғи', en: 'Beaumont Oak Natural' }, code: 'LW 644-2', color: '#c4a982' },
      { name: { ru: 'Дуб Бомонт Лофт', kz: 'Бомонт Еменi Лофт', en: 'Beaumont Oak Loft' }, code: 'LW 646-2', color: '#8b7a65' },
      { name: { ru: 'Дуб Седан Натуральный', kz: 'Седан Еменi Табиғи', en: 'Sedan Oak Natural' }, code: 'LW 724-2', color: '#b89d7a' },
      { name: { ru: 'Дуб Седан Сиеста', kz: 'Седан Еменi Сиеста', en: 'Sedan Oak Siesta' }, code: 'LW 727-2', color: '#7a6552' },
    ],
  },
  superPremium: {
    name: { ru: 'Супер-Премиум', kz: 'Супер-Премиум', en: 'Super-Premium' },
    textures: [
      { name: { ru: 'Дуб Бодега Светлый', kz: 'Бодега Еменi Ашық', en: 'Bodega Oak Light' }, code: 'CW 271-3-L', color: '#d4c4a8' },
      { name: { ru: 'Дуб Бодега Натуральный', kz: 'Бодега Еменi Табиғи', en: 'Bodega Oak Natural' }, code: 'CW 274-3-L', color: '#b5a082' },
      { name: { ru: 'Дуб Бодега Золотой', kz: 'Бодега Еменi Алтын', en: 'Bodega Oak Golden' }, code: 'CW 275-3-L', color: '#c9a65c' },
      { name: { ru: 'Дуб Бодега Коричневый', kz: 'Бодега Еменi Қоңыр', en: 'Bodega Oak Brown' }, code: 'CW 278-SFT-L', color: '#7a5c3d' },
      { name: { ru: 'Скол Камня Грей', kz: 'Тас Жарығы Сұр', en: 'Stone Chip Grey' }, code: 'NSN 133-N-L', color: '#9a9a9a' },
      { name: { ru: 'Скол Камня Графит', kz: 'Тас Жарығы Графит', en: 'Stone Chip Graphite' }, code: 'NSN 136-N-L', color: '#4d4d4d' },
      { name: { ru: 'Травертин Светлый', kz: 'Травертин Ашық', en: 'Travertine Light' }, code: 'NSN 111-N-L', color: '#e8dcc8' },
    ],
  },
  luxury: {
    name: { ru: 'Люкс', kz: 'Люкс', en: 'Luxury' },
    textures: [
      { name: { ru: 'Орех Американский Мокко', kz: 'Американ Жаңғақ Мокко', en: 'American Walnut Mokko' }, code: 'LW 735-SFT', color: '#5c4033' },
      { name: { ru: 'Орех Американский Шоколад', kz: 'Американ Жаңғақ Шоколад', en: 'American Walnut Chocolate' }, code: 'LW 737-SFT', color: '#3d2817' },
      { name: { ru: 'Орех Американский Натуральный', kz: 'Американ Жаңғақ Табиғи', en: 'American Walnut Natural' }, code: 'LW 733-SFT', color: '#6b5344' },
      { name: { ru: 'Шпон Орех Натуральный Диагональ', kz: 'Жаңғақ Табиғи Диагональ', en: 'Natural Walnut Veneer Diagonal' }, code: 'LW 254-DLSFT', color: '#6b5344' },
      { name: { ru: 'Кварц Лайт Грей', kz: 'Кварц Ашық Сұр', en: 'Quartz Light Grey' }, code: 'CRM 122-QZ-L', color: '#c8c8c8' },
      { name: { ru: 'Кварц Грей', kz: 'Кварц Сұр', en: 'Quartz Grey' }, code: 'CRM 127-QZ-L', color: '#8a8a8a' },
    ],
  },
  cashmere: {
    name: { ru: 'Кашемир', kz: 'Кашемир', en: 'Cashmere' },
    textures: [
      { name: { ru: 'Нежные облака', kz: 'Нәзік бұлттар', en: 'Gentle Clouds' }, code: 'F422721-804-P5', color: '#f2ece0', newUntil: '2026-12-14' },
      { name: { ru: 'Пудровый рассвет', kz: 'Пудра таңы', en: 'Powder Dawn' }, code: 'F61466-804-P5', color: '#e6e4d7', newUntil: '2026-12-14' },
      { name: { ru: 'Дикая степь', kz: 'Жабайы дала', en: 'Wild Steppe' }, code: 'F7320-804-P5', color: '#797546', newUntil: '2026-12-14' },
      { name: { ru: 'Хвойный лес', kz: 'Қылқан жапырақты орман', en: 'Coniferous Forest' }, code: 'F7321-804-P5', color: '#294a37', newUntil: '2026-12-14' },
      { name: { ru: 'Ночное небо', kz: 'Түнгі аспан', en: 'Night Sky' }, code: 'F91191-804-P5', color: '#595b62', newUntil: '2026-12-14' },
      { name: { ru: 'Лёгкий туман', kz: 'Жеңіл тұман', en: 'Light Mist' }, code: 'F91192-804-P5', color: '#c9c9c7', newUntil: '2026-12-14' },
      { name: { ru: 'Дымчатое небо', kz: 'Түтінді аспан', en: 'Smoky Sky' }, code: 'F91222-804-P5', color: '#939491', newUntil: '2026-12-14' },
      { name: { ru: 'Светлое небо', kz: 'Ашық аспан', en: 'Bright Sky' }, code: 'F91221-804-P5', color: '#e2e1dd', newUntil: '2026-12-14' },
    ],
  },
};

const collectionOrder = ['oriental', 'grey', 'standard', 'cashmere', 'premium', 'superPremium', 'luxury', 'nomad'];

function CatalogTab({ token }) {
  const { language } = useLanguage();
  const t = texts[language] || texts.ru;
  const [activeCollection, setActiveCollection] = React.useState('all');
  const [catalogImages, setCatalogImages] = React.useState({});
  const [search, setSearch] = React.useState('');
  const [selected, setSelected] = React.useState(null);

  React.useEffect(() => {
    fetch(`${API_URL}/api/catalog/images`)
      .then(r => r.json())
      .then(data => {
        const map = {};
        data.forEach(img => { map[`${img.category}_${img.texture_code}`] = img.image_url; });
        setCatalogImages(map);
      })
      .catch(() => {});
  }, []);

  const getTextures = () => {
    if (activeCollection === 'all') return null;
    const col = textureCollections[activeCollection];
    if (!col) return [];
    if (col.textures) return col.textures;
    // Nomad: generate combinations
    if (col.ornaments && col.colors) {
      const textures = [];
      col.ornaments.forEach(ornament => {
        col.colors.forEach(color => {
          textures.push({
            name: { ru: `${ornament.name.ru} - ${color.name.ru}`, kz: `${ornament.name.kz} - ${color.name.kz}`, en: `${ornament.name.en} - ${color.name.en}` },
            code: `${ornament.code}-${color.code}`,
            color: color.color,
          });
        });
      });
      return textures;
    }
    return [];
  };

  const filterTextures = (textures) => {
    if (!search) return textures;
    const words = search.toLowerCase().split(/\s+/).filter(Boolean);
    return textures.filter(t => {
      const text = `${t.name[language] || t.name.ru} ${t.code}`.toLowerCase();
      return words.every(w => text.includes(w));
    });
  };

  const TexCard = ({ tex, colKey }) => {
    const imgKey = `${colKey}_${tex.code}`;
    const imgUrl = catalogImages[imgKey] || tex.image;
    return (
      <div onClick={() => setSelected({ ...tex, imgKey })} className="rounded-xl overflow-hidden bg-white border border-[#061d36]/10 hover:shadow-lg transition-shadow group cursor-pointer" data-testid={`dealer-texture-${tex.code}`}>
        <div className="aspect-square relative" style={imgUrl ? {} : { backgroundColor: tex.color }}>
          {imgUrl && <img src={imgUrl} alt={tex.name[language]} className="w-full h-full object-cover" loading="lazy" />}
          {isNew(tex.newUntil) && <NewBadge className="absolute top-2 left-2 z-10" size="md" />}
          <span className={`absolute bottom-3 left-3 text-xs font-bold ${imgUrl ? 'text-white drop-shadow-lg' : isLightColor(tex.color) ? 'text-[#061d36]' : 'text-white'}`}>APT: {tex.code}</span>
        </div>
        <div className="px-3 py-2.5">
          <p className="font-medium text-sm text-[#061d36] leading-tight">{tex.name[language]}</p>
        </div>
      </div>
    );
  };

  return (
    <div data-testid="dealer-catalog">
      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setSelected(null)} data-testid="texture-modal">
          <div className="bg-white rounded-2xl max-w-lg w-full overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="h-72 relative" style={(catalogImages[selected.imgKey] || selected.image) ? {} : { backgroundColor: selected.color }}>
              {(catalogImages[selected.imgKey] || selected.image) && <img src={catalogImages[selected.imgKey] || selected.image} alt={selected.name[language]} className="w-full h-full object-cover" />}
              <button onClick={() => setSelected(null)} className="absolute top-4 right-4 p-2 bg-black/30 hover:bg-black/50 rounded-full text-white transition-colors" data-testid="modal-close">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="p-6">
              <div className="flex items-center gap-2 mb-1">
                <p className="text-sm text-[#061d36]/50">APT: {selected.code}</p>
                {isNew(selected.newUntil) && <NewBadge size="sm" />}
              </div>
              <h3 className="text-xl font-bold text-[#061d36] mb-4">{selected.name[language]}</h3>
              <a
                href={`https://wa.me/77076792233?text=${encodeURIComponent(language === 'ru' ? `Здравствуйте! Хочу заказать образец панели:\nНазвание: ${selected.name[language]}\nАртикул: ${selected.code}` : `Hello! I'd like to order a sample:\nName: ${selected.name[language]}\nArticle: ${selected.code}`)}`}
                target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#25D366] hover:bg-[#1da851] text-white rounded-xl font-semibold transition-colors text-sm"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                {language === 'en' ? 'Order sample' : 'Заказать образец'}
              </a>
            </div>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-[#061d36]">{t.catalogTitle}</h2>
        </div>
        <input type="text" data-testid="catalog-search" value={search} onChange={e => setSearch(e.target.value)} placeholder={language === 'en' ? 'Search textures...' : 'Поиск текстур...'} className="px-3 py-2 border border-[#061d36]/10 rounded-lg text-sm w-full sm:w-64 focus:outline-none focus:border-[#8b4444]" />
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        <button onClick={() => setActiveCollection('all')} className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${activeCollection === 'all' ? 'bg-[#061d36] text-white' : 'bg-[#061d36]/5 text-[#061d36]/70 hover:bg-[#061d36]/10'}`}>{language === 'en' ? 'All' : 'Все'}</button>
        {collectionOrder.map(key => (
          <button key={key} onClick={() => setActiveCollection(key)} className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${activeCollection === key ? 'bg-[#061d36] text-white' : 'bg-[#061d36]/5 text-[#061d36]/70 hover:bg-[#061d36]/10'}`}>
            {textureCollections[key].name[language]}
          </button>
        ))}
      </div>

      {activeCollection === 'all' ? (
        <div className="space-y-8">
          {collectionOrder.map(key => {
            const col = textureCollections[key];
            let textures = col.textures || [];
            if (col.ornaments && col.colors) {
              textures = [];
              col.ornaments.forEach(ornament => {
                col.colors.forEach(color => {
                  textures.push({
                    name: { ru: `${ornament.name.ru} - ${color.name.ru}`, kz: `${ornament.name.kz} - ${color.name.kz}`, en: `${ornament.name.en} - ${color.name.en}` },
                    code: `${ornament.code}-${color.code}`,
                    color: color.color,
                  });
                });
              });
            }
            const filtered = filterTextures(textures);
            if (filtered.length === 0) return null;
            return (
              <div key={key}>
                <h3 className="text-lg font-bold text-[#061d36] mb-4">{col.name[language]}</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {filtered.map((tex, i) => <TexCard key={i} tex={tex} colKey={key} />)}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {filterTextures(getTextures()).map((tex, i) => <TexCard key={i} tex={tex} colKey={activeCollection} />)}
        </div>
      )}
    </div>
  );
}

function DocsTab() {
  const { language } = useLanguage();
  const t = texts[language] || texts.ru;

  const docs = [
    { name: { ru: 'Каталог панелей Гипсовинил', kz: 'Гипсовинил панельдер каталогы', en: 'Gipsovinil Panels Catalog' }, type: 'PDF', category: 'catalog', url: '/pdf/katalog-panelei-gipsovinil.pdf' },
    { name: { ru: 'Каталог орнаментов Гипсовинил', kz: 'Гипсовинил орнаменттер каталогы', en: 'Gipsovinil Ornaments Catalog' }, type: 'PDF', category: 'catalog', url: '/pdf/katalog-ornamentov-gipsovinil.pdf' },
    { name: { ru: 'Каталог профилей Гипсовинил', kz: 'Гипсовинил профильдер каталогы', en: 'Gipsovinil Profiles Catalog' }, type: 'PDF', category: 'catalog', url: '/pdf/katalog-profilei-gipsovinil.pdf' },
    { name: { ru: 'Презентация ТОО Гипсовинил', kz: 'ЖШС Гипсовинил презентациясы', en: 'Gipsovinil Company Presentation' }, type: 'PDF', category: 'company', url: `${API_URL}/api/docs/prezentaciya-gipsovinil.pdf` },
    { name: { ru: 'Альбом технических решений №1', kz: 'Техникалық шешімдер альбомы №1', en: 'Technical Solutions Album #1' }, type: 'PDF', category: 'tech', url: `${API_URL}/api/docs/albom-tekhnicheskih-reshenii-1.pdf` },
    { name: { ru: 'Альбом технических решений №2', kz: 'Техникалық шешімдер альбомы №2', en: 'Technical Solutions Album #2' }, type: 'PDF', category: 'tech', url: `${API_URL}/api/docs/albom-tekhnicheskih-reshenii-2.pdf` },
    { name: { ru: 'Альбом технических решений №3', kz: 'Техникалық шешімдер альбомы №3', en: 'Technical Solutions Album #3' }, type: 'PDF', category: 'tech', url: `${API_URL}/api/docs/albom-tekhnicheskih-reshenii-3.pdf` },
    { name: { ru: 'Инструкция по перевозке, хранению, эксплуатации', kz: 'Тасымалдау, сақтау, пайдалану нұсқаулығы', en: 'Transport, Storage & Operation Manual' }, type: 'PDF', category: 'tech', url: `${API_URL}/api/docs/instrukciya-perevozka-hranenie-ekspluataciya.pdf` },
    { name: { ru: 'Декларация соответствия', kz: 'Сәйкестік декларациясы', en: 'Declaration of Conformity' }, type: 'PDF', category: 'cert', url: `${API_URL}/api/docs/deklaraciya-sootvetstviya.pdf` },
    { name: { ru: 'Протокол пожарных испытаний', kz: 'Өрт сынау хаттамасы', en: 'Fire Test Protocol' }, type: 'PDF', category: 'cert', url: `${API_URL}/api/docs/protokol-pozharnyh-ispytanii.pdf` },
    { name: { ru: 'Экспертное заключение ТМ Гипсовинил', kz: 'Гипсовинил ТМ сараптама қорытындысы', en: 'Expert Opinion TM Gipsovinil' }, type: 'PDF', category: 'cert', url: `${API_URL}/api/docs/ekspertnoe-zaklyuchenie.pdf` },
    { name: { ru: 'Протокол лабораторных испытаний №1', kz: 'Зертханалық сынау хаттамасы №1', en: 'Laboratory Test Protocol #1' }, type: 'PDF', category: 'cert', url: `${API_URL}/api/docs/protokol-lab-ispytanii-1.pdf` },
    { name: { ru: 'Протокол лабораторных испытаний №2', kz: 'Зертханалық сынау хаттамасы №2', en: 'Laboratory Test Protocol #2' }, type: 'PDF', category: 'cert', url: `${API_URL}/api/docs/protokol-lab-ispytanii-2.pdf` },
    { name: { ru: 'Сертификат СТ-KZ (казахский)', kz: 'СТ-KZ сертификаты (қазақша)', en: 'ST-KZ Certificate (Kazakh)' }, type: 'PDF', category: 'cert', url: `${API_URL}/api/docs/sertifikat-st-kz-kazakh.pdf` },
    { name: { ru: 'Сертификат СТ-KZ (русский)', kz: 'СТ-KZ сертификаты (орысша)', en: 'ST-KZ Certificate (Russian)' }, type: 'PDF', category: 'cert', url: `${API_URL}/api/docs/sertifikat-st-kz-rus.pdf` },
  ];

  const categories = {
    catalog: { ru: 'Каталоги', kz: 'Каталогтар', en: 'Catalogs' },
    company: { ru: 'О компании', kz: 'Компания туралы', en: 'Company' },
    tech: { ru: 'Технические документы', kz: 'Техникалық құжаттар', en: 'Technical Documents' },
    cert: { ru: 'Сертификаты и испытания', kz: 'Сертификаттар мен сынаулар', en: 'Certificates & Tests' },
  };

  const grouped = {};
  docs.forEach(d => {
    const cat = d.category || 'other';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(d);
  });

  return (
    <div data-testid="dealer-docs">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-[#061d36]">{t.docsTitle}</h2>
        <p className="text-[#061d36]/50 text-sm mt-1">{t.docsSubtitle}</p>
      </div>
      {Object.entries(grouped).map(([cat, items]) => (
        <div key={cat} className="mb-6">
          <h3 className="text-sm font-semibold text-[#061d36]/50 uppercase tracking-wide mb-3">{categories[cat]?.[language] || cat}</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            {items.map((doc, i) => (
              <a key={i} href={doc.url} target="_blank" rel="noopener noreferrer" className="bg-white rounded-xl border border-[#061d36]/10 p-4 flex items-center gap-4 hover:border-[#8b4444]/30 transition-colors cursor-pointer group" data-testid={`doc-file-${i}`}>
                <div className="w-12 h-12 rounded-xl bg-[#8b4444]/10 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-6 h-6 text-[#8b4444]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-[#061d36] break-words">{doc.name[language] || doc.name.ru}</p>
                  <p className="text-xs text-[#061d36]/40">{doc.type}</p>
                </div>
                <Download className="w-4 h-4 text-[#8b4444] opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
              </a>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function PricesTab() {
  const { language } = useLanguage();
  const t = texts[language] || texts.ru;

  const priceFiles = [
    { name: { ru: 'Проектный прайс по гипсовинилу 2026', kz: 'Гипсовинил бойынша жобалық прайс 2026', en: 'Gipsovinil Project Price 2026' }, type: 'PDF', date: '2026', url: 'https://customer-assets.emergentagent.com/job_eb6569d2-ff99-4f35-852f-a0f5ff64afe8/artifacts/nvda7ngm_%D0%9F%D1%80%D0%BE%D0%B5%D0%BA%D1%82%D0%BD%D1%8B%D0%B8%CC%86%20%D0%BF%D1%80%D0%B0%D0%B8%CC%86%D1%81%20%D0%BF%D0%BE%20%D0%B3%D0%B8%D0%BF%D1%81%D0%BE%D0%B2%D0%B8%D0%BD%D0%B8%D0%BB%D1%83%202026%20%281%29.pdf' },
    { name: { ru: 'Проектный прайс на панели Комфорт 2026', kz: 'Комфорт панельдеріне жобалық прайс 2026', en: 'Comfort Panel Project Price 2026' }, type: 'PDF', date: '2026', url: 'https://customer-assets.emergentagent.com/job_eb6569d2-ff99-4f35-852f-a0f5ff64afe8/artifacts/szy8zsm8_%D0%9F%D1%80%D0%BE%D0%B5%D0%BA%D1%82%D0%BD%D1%8B%D0%B8%CC%86%20%D0%BF%D1%80%D0%B0%D0%B8%CC%86%D1%81%20%D0%BD%D0%B0%20%D0%BF%D0%B0%D0%BD%D0%B5%D0%BB%D0%B8%20%D0%9A%D0%BE%D0%BC%D1%84%D0%BE%D1%80%D1%82%202026.pdf' },
    { name: { ru: 'Проектный прайс на потолочные панели 2026', kz: 'Төбе панельдеріне жобалық прайс 2026', en: 'Ceiling Panel Project Price 2026' }, type: 'PDF', date: '2026', url: 'https://customer-assets.emergentagent.com/job_eb6569d2-ff99-4f35-852f-a0f5ff64afe8/artifacts/dijykrpc_%D0%9F%D1%80%D0%BE%D0%B5%D0%BA%D1%82%D0%BD%D1%8B%D0%B8%CC%86%20%D0%BF%D1%80%D0%B0%D0%B8%CC%86%D1%81%20%D0%BD%D0%B0%20%D0%BF%D0%BE%D1%82%D0%BE%D0%BB%D0%BE%D1%87%D0%BD%D1%8B%D0%B5%20%D0%BF%D0%B0%D0%BD%D0%B5%D0%BB%D0%B8%202026.pdf' },
    { name: { ru: 'Проектный прайс по профилям 2026', kz: 'Профильдер бойынша жобалық прайс 2026', en: 'Profiles Project Price 2026' }, type: 'PDF', date: '2026', url: 'https://customer-assets.emergentagent.com/job_eb6569d2-ff99-4f35-852f-a0f5ff64afe8/artifacts/v275f8bp_%D0%9F%D1%80%D0%BE%D0%B5%D0%BA%D1%82%D0%BD%D1%8B%D0%B8%CC%86%20%D0%BF%D1%80%D0%B0%D0%B8%CC%86%D1%81%20%D0%BF%D0%BE%20%D0%BF%D1%80%D0%BE%D1%84%D0%B8%D0%BB%D1%8F%D0%BC%202026%D0%B3..pdf' },
    { name: { ru: 'Nomad Дилерский', kz: 'Nomad Дилерлік', en: 'Nomad Dealer' }, type: 'PDF', date: '2026', url: 'https://customer-assets.emergentagent.com/job_eb6569d2-ff99-4f35-852f-a0f5ff64afe8/artifacts/s7zdmmzi_Nomad%20%D0%94%D0%B8%D0%BB%D0%B5%D1%80%D1%81%D0%BA%D0%B8%D0%B8%CC%86.pdf' },
    { name: { ru: 'Nomad Комфорт Дилерский', kz: 'Nomad Комфорт Дилерлік', en: 'Nomad Comfort Dealer' }, type: 'PDF', date: '2026', url: 'https://customer-assets.emergentagent.com/job_eb6569d2-ff99-4f35-852f-a0f5ff64afe8/artifacts/gvp84rpb_Nomad%20%D0%9A%D0%BE%D0%BC%D1%84%D0%BE%D1%80%D1%82%20%D0%94%D0%B8%D0%BB%D0%B5%D1%80%D1%81%D0%BA%D0%B8%D0%B8%CC%86.pdf' },
  ];

  return (
    <div data-testid="dealer-prices">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-[#061d36]">{t.pricesTitle}</h2>
        <p className="text-[#061d36]/50 text-sm mt-1">{t.pricesSubtitle}</p>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        {priceFiles.map((doc, i) => (
          <a key={i} href={doc.url} target="_blank" rel="noopener noreferrer" className="bg-white rounded-xl border border-[#061d36]/10 p-4 flex items-center gap-4 hover:border-[#8b4444]/30 transition-colors cursor-pointer group" data-testid={`price-file-${i}`}>
            <div className="w-12 h-12 rounded-xl bg-[#8b4444]/10 flex items-center justify-center flex-shrink-0">
              <FileText className="w-6 h-6 text-[#8b4444]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-[#061d36] break-words">{doc.name[language] || doc.name.ru}</p>
              <p className="text-xs text-[#061d36]/40">{doc.type} &middot; {doc.date}</p>
            </div>
            <Download className="w-4 h-4 text-[#8b4444] opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
          </a>
        ))}
      </div>
    </div>
  );
}

function ContractsTab() {
  const { language } = useLanguage();
  const t = texts[language] || texts.ru;

  const contracts = [
    { name: { ru: 'Дилерский договор (шаблон)', kz: 'Дилерлік шарт (үлгі)', en: 'Dealer Agreement (template)' }, type: 'DOCX', icon: 'contract' },
    { name: { ru: 'Приложение к договору — Спецификация', kz: 'Шартқа қосымша — Спецификация', en: 'Agreement Appendix — Specification' }, type: 'DOCX', icon: 'spec' },
    { name: { ru: 'Спецификация на панели ГКЛ', kz: 'ГКЛ панельдерінің спецификациясы', en: 'GKL Panel Specification' }, type: 'PDF', icon: 'spec' },
    { name: { ru: 'Спецификация на панели Комфорт', kz: 'Комфорт панельдерінің спецификациясы', en: 'Comfort Panel Specification' }, type: 'PDF', icon: 'spec' },
    { name: { ru: 'Спецификация на панели СМЛ', kz: 'СМЛ панельдерінің спецификациясы', en: 'SML Panel Specification' }, type: 'PDF', icon: 'spec' },
    { name: { ru: 'Акт приёма-передачи (шаблон)', kz: 'Қабылдау-тапсыру актісі (үлгі)', en: 'Acceptance Act (template)' }, type: 'DOCX', icon: 'contract' },
    { name: { ru: 'Коммерческое предложение (шаблон)', kz: 'Коммерциялық ұсыныс (үлгі)', en: 'Commercial Offer (template)' }, type: 'DOCX', icon: 'contract' },
  ];

  return (
    <div data-testid="dealer-contracts">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-[#061d36]">{t.contractsTitle}</h2>
        <p className="text-[#061d36]/50 text-sm mt-1">{t.contractsSubtitle}</p>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        {contracts.map((doc, i) => (
          <div key={i} className="bg-white rounded-xl border border-[#061d36]/10 p-4 flex items-center gap-4 hover:border-[#8b4444]/30 transition-colors cursor-pointer group">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${doc.icon === 'contract' ? 'bg-blue-500/10' : 'bg-purple-500/10'}`}>
              <ScrollText className={`w-6 h-6 ${doc.icon === 'contract' ? 'text-blue-600' : 'text-purple-600'}`} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-[#061d36] truncate">{doc.name[language] || doc.name.ru}</p>
              <p className="text-xs text-[#061d36]/40">{doc.type}</p>
            </div>
            <span className="text-xs text-[#8b4444] opacity-0 group-hover:opacity-100 transition-opacity">{t.download}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function StockTab({ token, isAgent, isAdmin, hidePrices }) {
  const { language } = useLanguage();
  const t = texts[language] || texts.ru;
  const [stock, setStock] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [search, setSearch] = React.useState('');
  const [showPrice, setShowPrice] = React.useState(false);
  const [saleMode, setSaleMode] = React.useState(false);
  const [collapsed, setCollapsed] = React.useState({});
  const [cart, setCart] = React.useState({});
  const [submitting, setSubmitting] = React.useState(false);
  const [orderResult, setOrderResult] = React.useState(null);
  const [downloadingInvoice, setDownloadingInvoice] = React.useState(false);
  const [stockUpdatedAt, setStockUpdatedAt] = React.useState(null);
  // Hard override: accounts with hide_prices may never see prices or place orders.
  const priceVisible = !hidePrices && showPrice;
  const orderingAllowed = !isAgent && !hidePrices;

  React.useEffect(() => {
    fetch(`${API_URL}/api/dealer-catalog/stock`, {
      headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include',
    }).then(r => r.json()).then(data => {
      setStock(data.stock || data);
      if (data.updated_at) setStockUpdatedAt(data.updated_at);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [token]);

  const addToCart = (item) => {
    const key = item.productHref;
    if (!key || item.available <= 0) return;
    const effectivePrice = (saleMode && item.on_sale) ? item.salePrice * 0.9 : item.salePrice;
    setCart(prev => {
      if (prev[key]) return prev;
      return { ...prev, [key]: { ...item, qty: 1, salePrice: effectivePrice } };
    });
  };

  const updateQty = (key, qty) => {
    setCart(prev => {
      const item = prev[key];
      if (!item) return prev;
      const clamped = Math.max(1, Math.min(qty, item.available));
      return { ...prev, [key]: { ...item, qty: clamped } };
    });
  };

  const removeFromCart = (key) => setCart(prev => { const n = { ...prev }; delete n[key]; return n; });

  const cartItems = Object.entries(cart);
  const cartTotal = cartItems.reduce((sum, [, c]) => sum + (c.salePrice * c.qty), 0);

  const submitOrder = async () => {
    if (cartItems.length === 0) return;
    setSubmitting(true);
    setOrderResult(null);
    try {
      const positions = cartItems.map(([, c]) => ({
        productHref: c.productHref,
        metaType: c.metaType || 'product',
        quantity: c.qty,
        price: c.salePrice,
      }));
      const res = await fetch(`${API_URL}/api/dealer/create-order`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ positions }),
      });
      const data = await res.json();
      if (res.ok) {
        setOrderResult(data);
        setCart({});
      } else {
        setOrderResult({ error: data.detail || (language === 'ru' ? 'Ошибка создания заказа' : 'Order creation failed') });
      }
    } catch (e) {
      setOrderResult({ error: language === 'ru' ? 'Не удалось подключиться к серверу' : 'Connection error' });
    }
    setSubmitting(false);
  };

  const downloadInvoice = async (orderId, orderName) => {
    setDownloadingInvoice(true);
    try {
      const res = await fetch(`${API_URL}/api/dealer-catalog/order/${orderId}/invoice`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Счёт_${orderName}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch {}
    setDownloadingInvoice(false);
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#8b4444]" /></div>;

  const filtered = search
    ? stock.filter(s => {
        const words = search.toLowerCase().split(/\s+/).filter(Boolean);
        const text = `${s.name} ${s.article || ''} ${s.unit || ''}`.toLowerCase();
        return words.every(w => text.includes(w));
      })
    : stock;

  const grouped = {};
  filtered.forEach(s => {
    const g = s.group || 'Прочее';
    if (!grouped[g]) grouped[g] = {};
    const sub = s.subgroup || g;
    if (!grouped[g][sub]) grouped[g][sub] = [];
    grouped[g][sub].push(s);
  });

  const groupOrder = Object.keys(grouped).sort();
  const toggleGroup = (g) => setCollapsed(prev => ({ ...prev, [g]: !prev[g] }));

  const groupColors = {
    'Сырье': { bg: 'bg-white', border: 'border-2 border-[#8b4444]', text: 'text-[#8b4444]', badge: 'bg-[#8b4444]/10 text-[#8b4444]' },
  };
  const defaultColor = { bg: 'bg-white', border: 'border-2 border-[#061d36]/50', text: 'text-[#061d36]', badge: 'bg-[#061d36]/10 text-[#061d36]' };
  const colCount = showPrice ? 7 : 6;

  const hiddenOrderGroups = new Set(['Сырье']);

  return (
    <div data-testid="dealer-stock">
      {/* Order success banner */}
      {orderResult && !orderResult.error && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-xl p-4 flex items-center justify-between flex-wrap gap-3" data-testid="order-success">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center"><Check className="w-5 h-5 text-green-600" /></div>
            <div>
              <p className="font-semibold text-green-800">{language === 'en' ? 'Order created!' : 'Заказ создан!'} #{orderResult.order_name}</p>
              <p className="text-green-600 text-sm">{language === 'en' ? 'Total' : 'Сумма'}: {Math.round(orderResult.sum).toLocaleString()} ₸</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => downloadInvoice(orderResult.order_id, orderResult.order_name)} disabled={downloadingInvoice}
              className="flex items-center gap-1.5 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50" data-testid="download-new-invoice">
              {downloadingInvoice ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              {language === 'en' ? 'Download invoice' : 'Скачать счёт'}
            </button>
            <button onClick={() => setOrderResult(null)} className="px-3 py-2 text-green-700 hover:bg-green-100 rounded-lg text-sm transition-colors">&times;</button>
          </div>
        </div>
      )}
      {orderResult && orderResult.error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-center justify-between" data-testid="order-error">
          <p className="font-semibold text-red-700">{orderResult.error}</p>
          <button onClick={() => setOrderResult(null)} className="px-3 py-2 text-red-700 hover:bg-red-100 rounded-lg text-sm transition-colors">&times;</button>
        </div>
      )}

      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-[#061d36]">{t.stockTitle}</h2>
          <p className="text-[#061d36]/50 text-sm mt-1">
            {t.stockSubtitle} &middot; {filtered.length} {language === 'en' ? 'items' : 'позиций'}
            {stockUpdatedAt && (
              <span className="ml-2 text-[#061d36]/30" data-testid="stock-updated-at">
                &middot; {language === 'ru' ? 'Обновлено' : 'Updated'}: {new Date(stockUpdatedAt).toLocaleString(language === 'en' ? 'en-GB' : 'ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {!isAgent && !hidePrices && (
            <button
              data-testid="toggle-sale-btn"
              onClick={() => setSaleMode(p => !p)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                saleMode
                  ? 'bg-orange-500 text-white'
                  : 'bg-orange-50 text-orange-600 border border-orange-200 hover:bg-orange-100'
              }`}
            >
              <Tag className="w-4 h-4" />
              {t.saleBtn}
            </button>
          )}
          {!isAgent && !hidePrices && <button
            data-testid="toggle-price-btn"
            onClick={() => setShowPrice(p => !p)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${showPrice ? 'bg-[#8b4444] text-white' : 'bg-[#061d36]/5 text-[#061d36]/70 hover:bg-[#061d36]/10'}`}
          >
            <span className="w-4 h-4 flex items-center justify-center font-bold text-sm leading-none">₸</span>
            {showPrice ? t.hidePrice : t.showPrice}
          </button>}
          <input type="text" data-testid="stock-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="..." className="px-3 py-2 border border-[#061d36]/10 rounded-lg text-sm w-full sm:w-64 focus:outline-none focus:border-[#8b4444]" />
        </div>
      </div>

      {/* Cart panel */}
      {orderingAllowed && cartItems.length > 0 && (
        <div className="mb-6 bg-[#061d36] rounded-xl p-4 text-white" data-testid="order-cart">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5" />
              <span className="font-semibold">{language === 'en' ? 'Order' : 'Заказ'} ({cartItems.length} {language === 'en' ? 'items' : 'поз.'})</span>
            </div>
            <span className="font-bold text-lg">{Math.round(cartTotal).toLocaleString()} ₸</span>
          </div>
          <div className="space-y-2 mb-3 max-h-48 overflow-y-auto">
            {cartItems.map(([key, c]) => (
              <div key={key} className="flex items-center gap-2 bg-white/10 rounded-lg px-3 py-2 text-sm">
                <span className="flex-1 truncate">{c.name}</span>
                <div className="flex items-center gap-1">
                  <button onClick={() => updateQty(key, c.qty - 1)} className="p-0.5 hover:bg-white/20 rounded"><Minus className="w-3.5 h-3.5" /></button>
                  <input type="number" value={c.qty} onChange={e => updateQty(key, Number(e.target.value))} min={1} max={c.available}
                    className="w-14 text-center bg-white/10 rounded px-1 py-0.5 text-sm tabular-nums [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
                  <button onClick={() => updateQty(key, c.qty + 1)} className="p-0.5 hover:bg-white/20 rounded"><Plus className="w-3.5 h-3.5" /></button>
                </div>
                <span className="w-24 text-right font-medium whitespace-nowrap">{Math.round(c.salePrice * c.qty).toLocaleString()} ₸</span>
                <button onClick={() => removeFromCart(key)} className="p-1 hover:bg-red-500/30 rounded"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
          <button onClick={submitOrder} disabled={submitting}
            className="w-full py-2.5 bg-[#8b4444] hover:bg-[#7a3838] text-white rounded-lg font-semibold text-sm transition-colors disabled:opacity-50 flex items-center justify-center gap-2" data-testid="submit-order-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingCart className="w-4 h-4" />}
            {language === 'en' ? 'Submit order to MoySklad' : 'Отправить заказ в МойСклад'}
          </button>
        </div>
      )}

      <div className="space-y-4">
        {groupOrder.map(group => {
          const color = groupColors[group] || defaultColor;
          const subgroups = grouped[group];
          const subKeys = Object.keys(subgroups).sort();
          const groupCount = subKeys.reduce((sum, k) => sum + subgroups[k].length, 0);
          const isCollapsed = collapsed[group];

          return (
            <div key={group} className={`rounded-xl border ${color.border} overflow-hidden`} data-testid={`stock-group-${group}`}>
              <button
                onClick={() => toggleGroup(group)}
                className={`w-full flex items-center justify-between px-5 py-3 ${color.bg} hover:opacity-90 transition-opacity`}
              >
                <div className="flex items-center gap-3">
                  {isCollapsed ? <ChevronRight className={`w-5 h-5 ${color.text}`} /> : <ChevronDown className={`w-5 h-5 ${color.text}`} />}
                  <span className={`font-bold text-base ${color.text}`}>{group}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${color.badge}`}>{groupCount}</span>
                </div>
              </button>

              {!isCollapsed && (
                <div className="bg-white overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
                  <table className="text-sm w-full min-w-[720px] sm:min-w-0" style={{ tableLayout: 'fixed' }}>
                    <colgroup>
                      {priceVisible ? (
                        <>{/* Name | Price | Unit | Stock | Reserve | Available | Action */}
                        <col style={{ minWidth: '200px', width: 'auto' }} />
                        <col style={{ width: '100px' }} />
                        <col style={{ width: '55px' }} />
                        <col style={{ width: '80px' }} />
                        <col style={{ width: '70px' }} />
                        <col style={{ width: '80px' }} />
                        <col style={{ width: '110px' }} />
                        </>
                      ) : (
                        <>{/* Name | Unit | Stock | Reserve | Available | Action */}
                        <col style={{ minWidth: '200px', width: 'auto' }} />
                        <col style={{ width: '55px' }} />
                        <col style={{ width: '80px' }} />
                        <col style={{ width: '70px' }} />
                        <col style={{ width: '80px' }} />
                        <col style={{ width: '110px' }} />
                        </>
                      )}
                    </colgroup>
                    <thead>
                      <tr className="bg-[#061d36]/[0.04] text-[#061d36]/50 text-left text-xs border-b border-[#061d36]/10">
                        <th className="px-5 py-2.5 font-semibold">{t.stockName}</th>
                        {priceVisible && <th className="px-3 py-2.5 font-semibold text-right">{t.stockPrice}</th>}
                        <th className="px-3 py-2.5 font-semibold text-center">{t.stockUnit}</th>
                        <th className="px-3 py-2.5 font-semibold text-right">{t.stockQty}</th>
                        <th className="px-3 py-2.5 font-semibold text-right">{t.stockReserve}</th>
                        <th className="px-3 py-2.5 font-semibold text-right">{t.stockAvail}</th>
                        <th className="px-3 py-2.5 font-semibold text-center pr-5"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {subKeys.map(sub => {
                        const items = subgroups[sub];
                        const subLabel = sub.replace(`${group}/`, '');
                        const canOrder = orderingAllowed && !hiddenOrderGroups.has(group);
                        return (
                          <React.Fragment key={sub}>
                            {subKeys.length > 1 && (
                              <tr className="bg-[#061d36]/[0.02]">
                                <td colSpan={colCount} className="px-5 py-1.5">
                                  <span className="text-[11px] font-semibold text-[#061d36]/40 uppercase tracking-wider">{subLabel}</span>
                                </td>
                              </tr>
                            )}
                            {items.map((s, i) => {
                              const inCart = !!cart[s.productHref];
                              return (
                              <tr key={i} className={`border-t border-[#061d36]/5 transition-colors ${inCart ? 'bg-[#8b4444]/10' : 'hover:bg-[#8b4444]/5'}`}>
                                <td className="px-5 py-2.5 text-[#061d36] overflow-hidden text-ellipsis" title={s.name}>
                                  <span className="font-medium text-[#061d36]">{s.name}</span>
                                  {saleMode && s.on_sale && (
                                    <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-orange-100 text-orange-700 border border-orange-300 whitespace-nowrap" data-testid={`sale-badge-${i}`}>
                                      {t.saleBadge}
                                    </span>
                                  )}
                                </td>
                                {priceVisible && (
                                  <td className="px-3 py-2.5 text-right whitespace-nowrap">
                                    {s.salePrice > 0 ? (
                                      saleMode && s.on_sale ? (
                                        <div className="flex flex-col items-end leading-tight">
                                          <span className="text-orange-600 font-semibold">{Math.round(s.salePrice * 0.9).toLocaleString()} ₸</span>
                                          <span className="text-[#061d36]/30 text-xs line-through">{Math.round(s.salePrice).toLocaleString()} ₸</span>
                                        </div>
                                      ) : (
                                        <span className="text-[#8b4444] font-semibold">{Math.round(s.salePrice).toLocaleString()} ₸</span>
                                      )
                                    ) : '—'}
                                  </td>
                                )}
                                <td className="px-3 py-2.5 text-center text-[#061d36]/50 whitespace-nowrap">{s.unit || 'шт'}</td>
                                <td className="px-3 py-2.5 text-right text-[#061d36] tabular-nums whitespace-nowrap">{s.stock}</td>
                                <td className="px-3 py-2.5 text-right text-green-600 tabular-nums whitespace-nowrap">{s.reserve}</td>
                                <td className="px-3 py-2.5 text-right font-semibold text-[#8b4444] tabular-nums whitespace-nowrap">{s.available}</td>
                                <td className="px-3 py-2.5 text-center pr-5">
                                  {canOrder && s.available > 0 && s.productHref && (
                                    inCart ? (
                                      <span className="inline-flex items-center gap-1 text-[#8b4444] text-xs font-medium"><Check className="w-3.5 h-3.5" />{language === 'en' ? 'Added' : 'В заказе'}</span>
                                    ) : (
                                      <button onClick={() => addToCart(s)} className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#8b4444] hover:bg-[#7a3838] text-white rounded-lg text-xs font-medium transition-colors" data-testid={`add-to-cart-${i}`}>
                                        <Plus className="w-3 h-3" />{language === 'en' ? 'Order' : 'Заказать'}
                                      </button>
                                    )
                                  )}
                                </td>
                              </tr>
                              );
                            })}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function OrdersTab({ token, isAdmin }) {
  const { language } = useLanguage();
  const t = texts[language] || texts.ru;
  const [orders, setOrders] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [expandedOrder, setExpandedOrder] = React.useState(null);
  const [positions, setPositions] = React.useState({});
  const [search, setSearch] = React.useState('');
  const [downloading, setDownloading] = React.useState({});
  const [dealers, setDealers] = React.useState([]);
  const [selectedDealer, setSelectedDealer] = React.useState('');
  const [showDealerList, setShowDealerList] = React.useState(false);
  const [showDealerInfo, setShowDealerInfo] = React.useState(false);
  const [dealerInfoList, setDealerInfoList] = React.useState([]);
  const [previewInvoice, setPreviewInvoice] = React.useState(null);
  const [previewLoading, setPreviewLoading] = React.useState(false);
  const [updatedAt, setUpdatedAt] = React.useState(null);

  const downloadInvoice = async (orderId, orderName) => {
    setDownloading(prev => ({ ...prev, [orderId]: true }));
    try {
      const res = await fetch(`${API_URL}/api/dealer-catalog/order/${orderId}/invoice`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Счёт_${orderName}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch {}
    setDownloading(prev => ({ ...prev, [orderId]: false }));
  };

  const previewInvoiceFn = async (orderId, orderName) => {
    setPreviewLoading(true);
    setPreviewInvoice({ name: orderName, url: null });
    try {
      const res = await fetch(`${API_URL}/api/dealer-catalog/order/${orderId}/invoice`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        setPreviewInvoice({ name: orderName, url, orderId });
      } else {
        setPreviewInvoice(null);
      }
    } catch {
      setPreviewInvoice(null);
    }
    setPreviewLoading(false);
  };

  const closePreview = () => {
    if (previewInvoice?.url) window.URL.revokeObjectURL(previewInvoice.url);
    setPreviewInvoice(null);
  };

  React.useEffect(() => {
    fetch(`${API_URL}/api/dealer-catalog/orders`, {
      headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include',
    }).then(r => r.json()).then(data => {
      setOrders(data.orders || data);
      if (data.updated_at) setUpdatedAt(data.updated_at);
    }).catch(() => {}).finally(() => setLoading(false));
    if (isAdmin) {
      fetch(`${API_URL}/api/leads/dealers`, {
        headers: { 'Authorization': `Bearer ${token}` },
      }).then(r => r.json()).then(setDealers).catch(() => {});
    }
  }, [token, isAdmin]);

  const togglePositions = async (orderId) => {
    if (expandedOrder === orderId) { setExpandedOrder(null); return; }
    setExpandedOrder(orderId);
    if (!positions[orderId]) {
      try {
        const res = await fetch(`${API_URL}/api/dealer-catalog/order/${orderId}/positions`, {
          headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include',
        });
        if (res.ok) {
          const data = await res.json();
          setPositions(prev => ({ ...prev, [orderId]: data }));
        }
      } catch {}
    }
  };

  const openDealerInfo = async () => {
    try {
      const res = await fetch(`${API_URL}/api/dealer-auth/list-all`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) setDealerInfoList(await res.json());
    } catch {}
    setShowDealerInfo(true);
  };

  const filtered = orders.filter(o => {
    if (selectedDealer) {
      const dl = dealers.find(d => d.id === selectedDealer);
      if (dl && o.agent !== dl.company_name) return false;
    }
    if (search) {
      const words = search.toLowerCase().split(/\s+/).filter(Boolean);
      const text = `${o.name} ${o.agent || ''} ${o.state || ''}`.toLowerCase();
      return words.every(w => text.includes(w));
    }
    return true;
  });

  return (
    <div data-testid="dealer-orders">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-[#061d36]">{t.ordersTitle}</h2>
          <p className="text-[#061d36]/50 text-sm mt-1">
            {t.ordersSubtitle}{!loading && <> &middot; {filtered.length} {language === 'en' ? 'orders' : 'заказов'}</>}
            {updatedAt && (
              <span className="ml-2 text-[#061d36]/30" data-testid="orders-updated-at">
                &middot; {language === 'ru' ? 'Обновлено' : 'Updated'}: {new Date(updatedAt).toLocaleString(language === 'en' ? 'en-GB' : 'ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin && (
            <div className="relative">
              <button onClick={() => setShowDealerList(!showDealerList)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all border ${selectedDealer ? 'bg-[#061d36] text-white border-[#061d36]' : 'bg-white text-[#061d36]/60 border-[#061d36]/10 hover:border-[#061d36]/30'}`}
                data-testid="dealer-filter-btn">
                <Users className="w-4 h-4" />
                {selectedDealer ? (dealers.find(d => d.id === selectedDealer)?.short_name || dealers.find(d => d.id === selectedDealer)?.company_name) : (language === 'ru' ? 'Все дилеры' : 'All dealers')}
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showDealerList ? 'rotate-180' : ''}`} />
              </button>
              {showDealerList && (
                <div className="absolute z-50 top-full mt-1 left-0 bg-white border border-[#061d36]/10 rounded-xl shadow-xl min-w-[220px] max-h-60 overflow-y-auto">
                  <button onClick={() => { setSelectedDealer(''); setShowDealerList(false); }}
                    className={`w-full text-left px-4 py-2.5 text-sm transition-colors border-b border-[#061d36]/5 ${!selectedDealer ? 'bg-[#061d36] text-white font-semibold' : 'hover:bg-[#061d36]/5 text-[#061d36]'}`}>
                    {language === 'ru' ? 'Все дилеры' : 'All dealers'}
                  </button>
                  {dealers.filter(d => d.login !== 'diplomat').map(d => (
                    <button key={d.id} onClick={() => { setSelectedDealer(d.id); setShowDealerList(false); }}
                      className={`w-full text-left px-4 py-2.5 text-sm transition-colors border-b border-[#061d36]/5 last:border-b-0 ${selectedDealer === d.id ? 'bg-[#061d36]/10 text-[#061d36] font-semibold' : 'hover:bg-[#061d36]/5 text-[#061d36]'}`}>
                      {d.short_name || d.company_name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {isAdmin && (
            <button onClick={openDealerInfo}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-white text-[#061d36]/60 border border-[#061d36]/10 hover:border-[#061d36]/30 transition-all"
              data-testid="dealer-info-btn">
              <Users className="w-4 h-4" />
              {language === 'ru' ? 'Список дилеров' : 'Dealer List'}
            </button>
          )}
          <input type="text" data-testid="orders-search" value={search} onChange={e => setSearch(e.target.value)} placeholder={language === 'en' ? 'Search by number or name...' : 'Поиск по номеру или названию...'} className="px-3 py-2 border border-[#061d36]/10 rounded-lg text-sm w-72 focus:outline-none focus:border-[#8b4444]" />
        </div>
      </div>
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#8b4444]" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-[#061d36]/30">{search ? (language === 'en' ? 'Nothing found' : 'Ничего не найдено') : t.noOrders}</div>
      ) : (
        <div className="bg-white rounded-xl border border-[#061d36]/10 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#061d36]/5 text-[#061d36]/60 text-left">
                <th className="px-4 py-3 font-medium">{t.orderDate}</th>
                <th className="px-4 py-3 font-medium">{t.orderNum}</th>
                {isAdmin && <th className="px-4 py-3 font-medium">Дилер</th>}
                <th className="px-4 py-3 font-medium text-center">{t.orderStatus}</th>
                <th className="px-4 py-3 font-medium text-right">{t.orderSum}</th>
                <th className="px-4 py-3 font-medium text-right">Оплачено</th>
                <th className="px-4 py-3 font-medium text-right">Оплатить</th>
                <th className="px-4 py-3 font-medium text-center">Позиции</th>
                <th className="px-4 py-3 font-medium text-center">{language === 'en' ? 'Invoice' : 'Счёт'}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((o) => {
                const remainder = o.sum - o.payedSum;
                return (
                  <React.Fragment key={o.id}>
                    <tr className="border-t border-[#061d36]/5 hover:bg-[#8b4444]/5">
                      <td className="px-4 py-3 text-[#061d36]/60">{o.created ? new Date(o.created).toLocaleDateString() : ''}</td>
                      <td className="px-4 py-3 font-medium text-[#061d36]">{o.name}</td>
                      {isAdmin && <td className="px-4 py-3 text-[#061d36]/60 text-sm">{o.agent}</td>}
                      <td className="px-4 py-3 text-center">
                        <span className="px-2.5 py-1 rounded-full text-xs font-medium text-white" style={{ backgroundColor: o.stateColor || '#061d36' }}>
                          {o.state || '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-[#061d36]">{o.sum > 0 ? `${o.sum.toLocaleString()} ₸` : '-'}</td>
                      <td className="px-4 py-3 text-right text-green-600">{o.payedSum > 0 ? `${o.payedSum.toLocaleString()} ₸` : '0'}</td>
                      <td className="px-4 py-3 text-right font-semibold text-[#8b4444]">{remainder > 0 ? `${remainder.toLocaleString()} ₸` : '0'}</td>
                      <td className="px-4 py-3 text-center">
                        <button onClick={() => togglePositions(o.id)} className="text-[#061d36] hover:underline text-xs font-medium">
                          {expandedOrder === o.id ? 'Скрыть' : 'Позиции'}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="inline-flex items-center gap-2">
                          <button
                            data-testid={`preview-invoice-${o.name}`}
                            onClick={() => previewInvoiceFn(o.id, o.name)}
                            className="inline-flex items-center gap-1 text-[#061d36]/60 hover:text-[#8b4444] text-xs font-medium transition-colors"
                            title={language === 'ru' ? 'Просмотр' : 'Preview'}
                          >
                            <Eye className="w-3.5 h-3.5" />
                          </button>
                          <button
                            data-testid={`download-invoice-${o.name}`}
                            onClick={() => downloadInvoice(o.id, o.name)}
                            disabled={downloading[o.id]}
                            className="inline-flex items-center gap-1 text-[#061d36]/60 hover:text-[#8b4444] text-xs font-medium transition-colors disabled:opacity-40"
                            title={language === 'ru' ? 'Скачать' : 'Download'}
                          >
                            {downloading[o.id] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expandedOrder === o.id && (
                      <tr>
                        <td colSpan={isAdmin ? "9" : "8"} className="bg-[#061d36]/3 px-6 py-3">
                          {!positions[o.id] ? (
                            <div className="flex justify-center py-2"><Loader2 className="w-5 h-5 animate-spin text-[#8b4444]" /></div>
                          ) : (positions[o.id].positions || positions[o.id]).length === 0 ? (
                            <p className="text-[#061d36]/30 text-xs text-center">Нет позиций</p>
                          ) : (() => {
                            const posData = positions[o.id];
                            const items = posData.positions || posData;
                            const tw = posData.totalWeight || 0;
                            const tv = posData.totalVolume || 0;
                            return (
                              <div>
                                {(tw > 0 || tv > 0) && (
                                  <div className="flex gap-4 mb-2 text-xs">
                                    {tw > 0 && <span className="px-2.5 py-1 bg-[#061d36]/10 rounded-lg font-medium text-[#061d36]">{language === 'en' ? 'Weight' : 'Вес'}: {tw.toLocaleString()} кг</span>}
                                    {tv > 0 && <span className="px-2.5 py-1 bg-[#061d36]/10 rounded-lg font-medium text-[#061d36]">{language === 'en' ? 'Volume' : 'Объём'}: {tv.toLocaleString()} м³</span>}
                                  </div>
                                )}
                                <table className="w-full text-xs">
                                  <thead><tr className="text-[#061d36]/50"><th className="text-left py-1">Товар</th><th className="text-right py-1">Кол-во</th><th className="text-right py-1">Вес, кг</th><th className="text-right py-1">Объём, м³</th><th className="text-right py-1">Цена</th><th className="text-right py-1">Сумма</th></tr></thead>
                                  <tbody>
                                    {items.map((p, i) => (
                                      <tr key={i} className="border-t border-[#061d36]/5">
                                        <td className="py-1.5 text-[#061d36]">{p.name}</td>
                                        <td className="py-1.5 text-right">{p.quantity}</td>
                                        <td className="py-1.5 text-right text-[#061d36]/50">{p.weight > 0 ? p.weight.toLocaleString() : '—'}</td>
                                        <td className="py-1.5 text-right text-[#061d36]/50">{p.volume > 0 ? p.volume.toLocaleString() : '—'}</td>
                                        <td className="py-1.5 text-right">{p.price.toLocaleString()} ₸</td>
                                        <td className="py-1.5 text-right font-medium">{p.sum.toLocaleString()} ₸</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            );
                          })()}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Dealer Info Modal */}
      {showDealerInfo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
          onMouseDown={e => { if (e.target === e.currentTarget) setShowDealerInfo(false); }}>
          <div className="bg-white rounded-2xl w-full max-w-4xl shadow-2xl max-h-[85vh] overflow-hidden flex flex-col">
            <div className="p-5 border-b border-[#061d36]/10 flex items-center justify-between">
              <h3 className="text-lg font-bold text-[#061d36]">{language === 'ru' ? 'Список дилеров' : 'Dealer List'}</h3>
              <button onClick={() => setShowDealerInfo(false)} className="text-[#061d36]/30 hover:text-[#061d36] transition-colors">
                <span className="text-2xl leading-none">&times;</span>
              </button>
            </div>
            <div className="overflow-x-auto flex-1">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#061d36]/5 text-[#061d36]/60 text-left">
                    <th className="px-4 py-3 font-medium">Логин</th>
                    <th className="px-4 py-3 font-medium">Компания</th>
                    <th className="px-4 py-3 font-medium">Контакт</th>
                    <th className="px-4 py-3 font-medium">Телефон</th>
                    <th className="px-4 py-3 font-medium">Город</th>
                    <th className="px-4 py-3 font-medium text-center">Роль</th>
                    <th className="px-4 py-3 font-medium text-center">Статус</th>
                  </tr>
                </thead>
                <tbody>
                  {dealerInfoList.map((d, i) => (
                    <tr key={i} className="border-t border-[#061d36]/5 hover:bg-[#8b4444]/5">
                      <td className="px-4 py-3 font-medium text-[#061d36]">{d.login}</td>
                      <td className="px-4 py-3 text-[#061d36]">{d.company_name}</td>
                      <td className="px-4 py-3 text-[#061d36]/60">{d.contact_person}</td>
                      <td className="px-4 py-3 text-[#061d36]/60">{d.phone}</td>
                      <td className="px-4 py-3 text-[#061d36]/60">{d.city}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${d.role === 'admin' ? 'bg-[#8b4444]/10 text-[#8b4444]' : d.role === 'agent' ? 'bg-blue-100 text-blue-600' : 'bg-[#061d36]/10 text-[#061d36]/60'}`}>
                          {d.role === 'admin' ? 'Менеджер' : d.role === 'agent' ? 'Агент' : 'Дилер'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${d.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                          {d.is_active ? 'Активен' : 'Отключён'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Preview Modal */}
      {previewInvoice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
          data-testid="invoice-preview-modal"
          onMouseDown={e => { if (e.target === e.currentTarget) closePreview(); }}>
          <div className="bg-white rounded-2xl w-full max-w-4xl shadow-2xl flex flex-col" style={{ height: '85vh' }}>
            <div className="p-4 border-b border-[#061d36]/10 flex items-center justify-between">
              <h3 className="text-lg font-bold text-[#061d36]">
                {language === 'ru' ? 'Счёт' : 'Invoice'} {previewInvoice.name}
              </h3>
              <div className="flex items-center gap-2">
                {previewInvoice.url && (
                  <a href={previewInvoice.url} download={`Счёт_${previewInvoice.name}.pdf`}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-[#8b4444] hover:bg-[#7a3838] text-white rounded-lg text-sm font-medium transition-colors"
                    data-testid="invoice-preview-download">
                    <Download className="w-4 h-4" />
                    {language === 'ru' ? 'Скачать' : 'Download'}
                  </a>
                )}
                <button onClick={closePreview} className="text-[#061d36]/30 hover:text-[#061d36] transition-colors p-1" data-testid="invoice-preview-close">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden bg-[#061d36]/5 rounded-b-2xl">
              {previewLoading || !previewInvoice.url ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="w-8 h-8 animate-spin text-[#8b4444]" />
                </div>
              ) : (
                <iframe
                  src={previewInvoice.url}
                  title={`Invoice ${previewInvoice.name}`}
                  className="w-full h-full border-0"
                  data-testid="invoice-preview-iframe"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const TengeIcon = ({ className }) => <span className={`${className} flex items-center justify-center font-bold leading-none`}>₸</span>;

const tabConfig = [
  { key: 'objects', icon: MapPin },
  { key: 'catalog', icon: Package },
  { key: 'stock', icon: BarChart3 },
  { key: 'orders', icon: ShoppingCart },
  { key: 'prices', icon: TengeIcon },
  { key: 'contracts', icon: ScrollText },
  { key: 'calculator', icon: Calculator },
  { key: 'constructor', icon: Palette },
  { key: 'docs', icon: FileText },
];

export default function DealerDashboardPage() {
  const { dealer, loading, token, logout } = useDealer();
  const { language } = useLanguage();
  const t = texts[language] || texts.ru;
  const [activeTab, setActiveTab] = useState('crm');
  const [notifications, setNotifications] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [pleepLeads, setPleepLeads] = useState([]);
  const [assignedLeads, setAssignedLeads] = useState([]);
  const pleepSeenIdsRef = React.useRef(new Set());
  const assignedSeenIdsRef = React.useRef(new Set());
  const desktopNotifReadyRef = React.useRef(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifPanel, setShowNotifPanel] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const bannerShownRef = React.useRef(false);

  const isAdmin = dealer?.role === 'admin';

  // Global search state
  const [showSearch, setShowSearch] = useState(false);
  const [globalQuery, setGlobalQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchInputRef = React.useRef(null);
  const searchTimerRef = React.useRef(null);

  const doGlobalSearch = React.useCallback(async (q) => {
    if (!q || q.length < 2 || !token) {
      setSearchResults(null);
      return;
    }
    setSearchLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/cabinet/search?q=${encodeURIComponent(q)}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        // Also match tab names
        const tabMatches = [];
        const allTabNames = {
          crm: { ru: 'CRM', en: 'CRM', kz: 'CRM' },
          tasks: { ru: 'Задачник', en: 'Tasks', kz: 'Тапсырмалар' },
          objects: { ru: 'Объекты', en: 'Objects', kz: 'Нысандар' },
          catalog: { ru: 'Каталог', en: 'Catalog', kz: 'Каталог' },
          stock: { ru: 'Остатки', en: 'Stock', kz: 'Қалдықтар' },
          orders: { ru: 'Заказы дилеров', en: 'Dealer Orders', kz: 'Дилерлер тапсырыстары' },
          prices: { ru: 'Прайсы', en: 'Prices', kz: 'Бағалар' },
          calculator: { ru: 'Калькулятор', en: 'Calculator', kz: 'Калькулятор' },
          constructor: { ru: 'Визуализация', en: 'Visualization', kz: 'Визуализация' },
          docs: { ru: 'Документы', en: 'Documents', kz: 'Құжаттар' },
        };
        const ql = q.toLowerCase();
        Object.entries(allTabNames).forEach(([key, names]) => {
          if (Object.values(names).some(n => n.toLowerCase().includes(ql))) {
            tabMatches.push({ key, name: names[language] || names.ru });
          }
        });
        setSearchResults({ ...data, tabs: tabMatches });
      }
    } catch {}
    setSearchLoading(false);
  }, [token, language]);

  React.useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (globalQuery.length >= 2) {
      searchTimerRef.current = setTimeout(() => doGlobalSearch(globalQuery), 300);
    } else {
      setSearchResults(null);
    }
    return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
  }, [globalQuery, doGlobalSearch]);

  React.useEffect(() => {
    if (showSearch && searchInputRef.current) searchInputRef.current.focus();
  }, [showSearch]);

  const handleSearchSelect = (type, item) => {
    setShowSearch(false);
    setGlobalQuery('');
    setSearchResults(null);
    if (type === 'tab') setActiveTab(item.key);
    else if (type === 'lead') setActiveTab('crm');
    else if (type === 'task') setActiveTab('tasks');
    else if (type === 'object') setActiveTab('objects');
  };

  // Fetch notifications + reminders + pleep unseen
  React.useEffect(() => {
    if (!token) return;
    const headers = { 'Authorization': `Bearer ${token}` };

    // Request desktop notification permission once (admin/manager only — they get Pleep leads)
    if (!desktopNotifReadyRef.current && typeof window !== 'undefined' && 'Notification' in window) {
      if (Notification.permission === 'default') {
        try { Notification.requestPermission().catch(() => {}); } catch {}
      }
      desktopNotifReadyRef.current = true;
    }

    const showDesktopNotif = (lead) => {
      if (typeof window === 'undefined' || !('Notification' in window)) return;
      if (Notification.permission !== 'granted') return;
      try {
        const channelLabel = lead.channel === 'instagram' ? 'Instagram' : (lead.channel === 'whatsapp' ? 'WhatsApp' : 'Pleep');
        const phone = lead.phone ? ` · ${lead.phone}` : '';
        const tagText = lead.pleep_status ? ` (${lead.pleep_status})` : '';
        const n = new Notification(language === 'ru' ? 'Новый лид от Анель' : 'New Anel lead', {
          body: `${lead.name || ''}${phone}${tagText} — ${channelLabel}`,
          icon: '/logo-icon-dark.png',
          tag: `pleep-${lead.id}`,
          requireInteraction: false,
        });
        n.onclick = () => { window.focus(); setActiveTab('crm'); n.close(); };
      } catch {}
    };

    const showAssignedNotif = (lead) => {
      if (typeof window === 'undefined' || !('Notification' in window)) return;
      if (Notification.permission !== 'granted') return;
      try {
        const phone = lead.phone ? ` · ${lead.phone}` : '';
        const city = lead.city ? ` · ${lead.city}` : '';
        const n = new Notification(language === 'ru' ? 'Вам передан новый лид' : 'New lead assigned to you', {
          body: `${lead.name || ''}${phone}${city}`,
          icon: '/logo-icon-dark.png',
          tag: `assigned-${lead.id}`,
          requireInteraction: false,
        });
        n.onclick = () => { window.focus(); setActiveTab('crm'); n.close(); };
      } catch {}
    };

    const fetchNotifs = async () => {
      try {
        const res = await fetch(`${API_URL}/api/dealer/notifications`, { headers });
        if (res.ok) {
          const data = await res.json();
          setNotifications(data.notifications || []);
          const orderUnread = data.unread_count || 0;
          // Fetch reminders
          let rems = [];
          try {
            const rRes = await fetch(`${API_URL}/api/leads/reminders`, { headers });
            if (rRes.ok) { const rData = await rRes.json(); rems = rData.reminders || []; }
          } catch {}
          setReminders(rems);
          // Fetch Pleep unseen leads (admin/manager only — endpoint returns 403 otherwise)
          let pleepItems = [];
          try {
            const pRes = await fetch(`${API_URL}/api/leads/pleep/unseen`, { headers });
            if (pRes.ok) { const pData = await pRes.json(); pleepItems = pData.items || []; }
          } catch {}
          // Detect newly arrived (not in previous seen set) → trigger desktop notification
          const knownIds = pleepSeenIdsRef.current;
          const incomingIds = new Set(pleepItems.map(p => p.id));
          if (knownIds.size > 0) {
            // Skip first-load flood: only fire on subsequent polls
            pleepItems.forEach(item => {
              if (!knownIds.has(item.id)) showDesktopNotif(item);
            });
          }
          pleepSeenIdsRef.current = incomingIds;
          setPleepLeads(pleepItems);

          // Fetch assigned leads (dealer/agent only — 403 otherwise)
          let assignedItems = [];
          try {
            const aRes = await fetch(`${API_URL}/api/leads/assigned/unseen`, { headers });
            if (aRes.ok) { const aData = await aRes.json(); assignedItems = aData.items || []; }
          } catch {}
          const knownAssigned = assignedSeenIdsRef.current;
          const incomingAssigned = new Set(assignedItems.map(a => a.id));
          if (knownAssigned.size > 0) {
            assignedItems.forEach(item => {
              if (!knownAssigned.has(item.id)) showAssignedNotif(item);
            });
          }
          assignedSeenIdsRef.current = incomingAssigned;
          setAssignedLeads(assignedItems);

          const total = orderUnread + rems.length + pleepItems.length + assignedItems.length;
          setUnreadCount(total);
          if (total > 0 && !bannerShownRef.current) {
            setShowBanner(true);
            bannerShownRef.current = true;
            setTimeout(() => setShowBanner(false), 8000);
          }
        }
      } catch {}
    };
    fetchNotifs();
    const interval = setInterval(fetchNotifs, 60000);
    return () => clearInterval(interval);
  }, [token, language]);

  const markAllRead = async () => {
    const unreadIds = notifications.filter(n => !n.read).map(n => n.order_id);
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
    try {
      if (unreadIds.length > 0) {
        await fetch(`${API_URL}/api/dealer/notifications/read`, {
          method: 'POST', headers, body: JSON.stringify({ order_ids: unreadIds }),
        });
      }
      if (reminders.length > 0) {
        const leadIds = reminders.filter(r => r.type === 'lead').map(r => r.id);
        const taskIds = reminders.filter(r => r.type === 'task').map(r => r.id);
        await fetch(`${API_URL}/api/leads/reminders/dismiss`, {
          method: 'POST', headers, body: JSON.stringify({ lead_ids: leadIds, task_ids: taskIds }),
        });
        setReminders([]);
      }
      if (pleepLeads.length > 0) {
        await fetch(`${API_URL}/api/leads/pleep/mark-seen`, {
          method: 'POST', headers, body: JSON.stringify({ ids: pleepLeads.map(p => p.id) }),
        });
        // Do NOT clear pleepLeads here — keep showing them in the dropdown for the current session.
        // They'll naturally disappear on the next 30s polling cycle since backend now marks them seen.
      }
      if (assignedLeads.length > 0) {
        await fetch(`${API_URL}/api/leads/assigned/mark-seen`, {
          method: 'POST', headers, body: JSON.stringify({ ids: assignedLeads.map(a => a.id) }),
        });
      }
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {}
  };

  // Load catalog images for constructor
  const [constructorImages, setConstructorImages] = React.useState({});
  React.useEffect(() => {
    fetch(`${API_URL}/api/catalog/images`)
      .then(r => r.json())
      .then(data => {
        const map = {};
        data.forEach(img => { map[`${img.category}_${img.texture_code}`] = img.image_url; });
        setConstructorImages(map);
      })
      .catch(() => {});
  }, []);

  const isAgent = dealer?.role === 'agent';
  const hidePrices = !!dealer?.hide_prices;
  const hiddenTabs = new Set(Array.isArray(dealer?.hidden_tabs) ? dealer.hidden_tabs : []);

  const allTabs = [
    { key: 'crm', icon: ClipboardList },
    { key: 'tasks', icon: ListTodo },
    { key: 'objects', icon: MapPin },
    { key: 'catalog', icon: Package },
    { key: 'stock', icon: BarChart3 },
    ...(!isAgent ? [{ key: 'orders', icon: ShoppingCart }] : []),
    ...(!isAgent ? [{ key: 'prices', icon: TengeIcon }] : []),
    ...((dealer?.role === 'admin' || dealer?.role === 'manager') ? [{ key: 'calculator', icon: Calculator }] : []),
    { key: 'constructor', icon: Palette },
    { key: 'gallery', icon: ImageIcon },
    ...((dealer?.role === 'admin' || dealer?.role === 'manager') ? [
      { key: 'script', icon: BookOpen },
      { key: 'trainer', icon: Gamepad2 },
    ] : []),
    ...(dealer?.role === 'admin' ? [{ key: 'ai_stats', icon: BarChart3 }] : []),
    ...(dealer?.login === 'admin' ? [{ key: 'staff', icon: UserCog }] : []),
    { key: 'docs', icon: FileText },
  ].filter(tab => !hiddenTabs.has(tab.key));

  if (loading) return <div className="min-h-screen bg-[#F5F5F0] flex items-center justify-center"><Loader2 className="w-10 h-10 animate-spin text-[#8b4444]" /></div>;
  if (!dealer) return <Navigate to="/dealer/login" replace />;

  return (
    <div className="min-h-screen bg-[#F5F5F0] overflow-x-hidden" data-testid="dealer-dashboard">
      {/* Header bar */}
      <div className="bg-[#061d36] border-b border-white/10">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-2.5 shrink-0">
              <img src="/logo-icon-hq.png" alt="GIPSOVINIL.KZ" className="h-9 w-auto" />
              <img src="/logo-text-hq.png" alt="GIPSOVINIL.KZ" className="h-5 w-auto hidden sm:block" />
            </a>
            <div className="h-8 w-px bg-white/10 hidden xl:block" />
            <div className="hidden xl:flex items-center gap-4 text-white/50 text-xs">
              <a href="tel:+77472755088" className="flex items-center gap-1.5 hover:text-white transition-colors">
                <Phone className="w-3.5 h-3.5" />+7 747 275 5088 <span className="text-white/30">Айлана</span>
              </a>
              <a href="mailto:sales@gipsovinil.kz" className="flex items-center gap-1.5 hover:text-white transition-colors">
                <Mail className="w-3.5 h-3.5" />sales@gipsovinil.kz
              </a>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right mr-2 hidden lg:block">
              <p className="text-white font-semibold text-sm">{dealer.company_name}</p>
              <p className="text-white/40 text-xs">{dealer.contact_person} &middot; {dealer.city}</p>
            </div>
            {/* Global Search */}
            <div className="relative">
              <button onClick={() => { setShowSearch(s => !s); setShowNotifPanel(false); }}
                className="p-2 text-white/50 hover:text-white transition-colors" data-testid="global-search-btn">
                <Search className="w-5 h-5" />
              </button>
              {showSearch && (
                <div className="absolute right-0 top-full mt-2 w-[calc(100vw-2rem)] sm:w-96 bg-white rounded-xl shadow-2xl border border-[#061d36]/10 z-50 overflow-hidden" data-testid="global-search-panel">
                  <div className="p-3 border-b border-[#061d36]/10">
                    <div className="flex items-center gap-2 bg-[#061d36]/5 rounded-lg px-3 py-2">
                      <Search className="w-4 h-4 text-[#061d36]/40 flex-shrink-0" />
                      <input
                        ref={searchInputRef}
                        type="text"
                        value={globalQuery}
                        onChange={e => setGlobalQuery(e.target.value)}
                        placeholder={language === 'ru' ? 'Поиск по кабинету...' : 'Search cabinet...'}
                        className="bg-transparent text-sm text-[#061d36] w-full outline-none placeholder:text-[#061d36]/30"
                        data-testid="global-search-input"
                        onKeyDown={e => { if (e.key === 'Escape') { setShowSearch(false); setGlobalQuery(''); setSearchResults(null); } }}
                      />
                      {globalQuery && (
                        <button onClick={() => { setGlobalQuery(''); setSearchResults(null); }} className="text-[#061d36]/30 hover:text-[#061d36]">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {searchLoading && (
                      <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-[#8b4444]" /></div>
                    )}
                    {!searchLoading && searchResults && (
                      <>
                        {searchResults.tabs?.length > 0 && (
                          <div>
                            <p className="px-4 pt-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-[#061d36]/30">{language === 'ru' ? 'Разделы' : 'Sections'}</p>
                            {searchResults.tabs.map(tab => (
                              <button key={tab.key} onClick={() => handleSearchSelect('tab', tab)}
                                className="w-full text-left px-4 py-2.5 hover:bg-[#8b4444]/5 transition-colors flex items-center gap-3" data-testid={`search-result-tab-${tab.key}`}>
                                <div className="w-7 h-7 rounded-lg bg-[#061d36]/10 flex items-center justify-center flex-shrink-0">
                                  <FileText className="w-3.5 h-3.5 text-[#061d36]/50" />
                                </div>
                                <span className="text-sm font-medium text-[#061d36]">{tab.name}</span>
                              </button>
                            ))}
                          </div>
                        )}
                        {searchResults.leads?.length > 0 && (
                          <div>
                            <p className="px-4 pt-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-[#061d36]/30">CRM</p>
                            {searchResults.leads.map(ld => (
                              <button key={ld.id} onClick={() => handleSearchSelect('lead', ld)}
                                className="w-full text-left px-4 py-2.5 hover:bg-[#8b4444]/5 transition-colors flex items-center gap-3" data-testid={`search-result-lead-${ld.id}`}>
                                <div className="w-7 h-7 rounded-lg bg-[#8b4444]/10 flex items-center justify-center flex-shrink-0">
                                  <ClipboardList className="w-3.5 h-3.5 text-[#8b4444]" />
                                </div>
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-[#061d36] truncate">{ld.name}</p>
                                  <p className="text-xs text-[#061d36]/40">{ld.phone} &middot; {ld.status}</p>
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                        {searchResults.tasks?.length > 0 && (
                          <div>
                            <p className="px-4 pt-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-[#061d36]/30">{language === 'ru' ? 'Задачи' : 'Tasks'}</p>
                            {searchResults.tasks.map(tk => (
                              <button key={tk.id} onClick={() => handleSearchSelect('task', tk)}
                                className="w-full text-left px-4 py-2.5 hover:bg-[#8b4444]/5 transition-colors flex items-center gap-3" data-testid={`search-result-task-${tk.id}`}>
                                <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                                  <ListTodo className="w-3.5 h-3.5 text-blue-500" />
                                </div>
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-[#061d36] truncate">{tk.title}</p>
                                  <p className="text-xs text-[#061d36]/40">{tk.status}</p>
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                        {searchResults.objects?.length > 0 && (
                          <div>
                            <p className="px-4 pt-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-[#061d36]/30">{language === 'ru' ? 'Объекты' : 'Objects'}</p>
                            {searchResults.objects.map(obj => (
                              <button key={obj.id} onClick={() => handleSearchSelect('object', obj)}
                                className="w-full text-left px-4 py-2.5 hover:bg-[#8b4444]/5 transition-colors flex items-center gap-3" data-testid={`search-result-object-${obj.id}`}>
                                <div className="w-7 h-7 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0">
                                  <MapPin className="w-3.5 h-3.5 text-green-600" />
                                </div>
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-[#061d36] truncate">{obj.name}</p>
                                  <p className="text-xs text-[#061d36]/40 truncate">{obj.address}</p>
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                        {searchResults.tabs?.length === 0 && searchResults.leads?.length === 0 && searchResults.tasks?.length === 0 && searchResults.objects?.length === 0 && (
                          <p className="text-center text-[#061d36]/30 text-sm py-6" data-testid="search-no-results">{language === 'ru' ? 'Ничего не найдено' : 'Nothing found'}</p>
                        )}
                      </>
                    )}
                    {!searchLoading && !searchResults && globalQuery.length < 2 && (
                      <p className="text-center text-[#061d36]/30 text-xs py-5">{language === 'ru' ? 'Введите от 2 символов' : 'Type at least 2 characters'}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
            {/* Notification bell */}
            <div className="relative">
              <button onClick={() => { setShowNotifPanel(p => !p); if (unreadCount > 0) markAllRead(); }}
                className="relative p-2 text-white/50 hover:text-white transition-colors" data-testid="notif-bell">
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-[#8b4444] text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-pulse">{unreadCount}</span>
                )}
              </button>
              {showNotifPanel && (
                <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-2xl border border-[#061d36]/10 z-50 max-h-96 overflow-hidden" data-testid="notif-panel">
                  <div className="px-4 py-3 border-b border-[#061d36]/10 flex items-center justify-between">
                    <span className="font-semibold text-[#061d36] text-sm">{language === 'en' ? 'Notifications' : 'Уведомления'}</span>
                    <button onClick={() => setShowNotifPanel(false)} className="text-[#061d36]/30 hover:text-[#061d36]"><X className="w-4 h-4" /></button>
                  </div>
                  <div className="overflow-y-auto max-h-72">
                    {assignedLeads.length > 0 && assignedLeads.map((a) => (
                      <button key={`assigned-${a.id}`} onClick={() => { setActiveTab('crm'); setShowNotifPanel(false); }}
                        className="w-full text-left px-4 py-3 border-b border-[#061d36]/5 bg-gradient-to-r from-emerald-500/10 to-transparent hover:bg-emerald-500/15 transition-colors"
                        data-testid={`notif-assigned-${a.id}`}>
                        <div className="flex items-center gap-2">
                          <Users className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
                          <span className="font-semibold text-sm text-[#061d36] truncate">{a.client_name || a.name}</span>
                          <span className="ml-auto text-[9px] uppercase tracking-wide text-emerald-700 font-bold">{language === 'ru' ? 'Новый' : 'New'}</span>
                        </div>
                        <p className="text-xs text-emerald-700 font-medium mt-1">
                          {language === 'ru' ? 'Вам передан лид' : 'Lead assigned to you'}
                        </p>
                        {(a.phone || a.city) && <p className="text-xs text-[#061d36]/50 mt-0.5">{a.phone}{a.city ? ` · ${a.city}` : ''}</p>}
                      </button>
                    ))}
                    {pleepLeads.length > 0 && pleepLeads.map((p, i) => (
                      <button key={`pleep-${p.id}`} onClick={() => { setActiveTab('crm'); setShowNotifPanel(false); }}
                        className="w-full text-left px-4 py-3 border-b border-[#061d36]/5 bg-gradient-to-r from-[#7c3aed]/10 to-transparent hover:bg-[#7c3aed]/15 transition-colors"
                        data-testid={`notif-pleep-${p.id}`}>
                        <div className="flex items-center gap-2">
                          {p.channel === 'instagram' ? <Instagram className="w-3.5 h-3.5 text-[#7c3aed] flex-shrink-0" /> : p.channel === 'whatsapp' ? <MessageCircle className="w-3.5 h-3.5 text-[#7c3aed] flex-shrink-0" /> : <Bot className="w-3.5 h-3.5 text-[#7c3aed] flex-shrink-0" />}
                          <span className="font-semibold text-sm text-[#061d36] truncate">{p.name || p.instagram_handle || p.phone}</span>
                          <span className="ml-auto text-[9px] uppercase tracking-wide text-[#7c3aed] font-bold">Pleep</span>
                        </div>
                        <p className="text-xs text-[#7c3aed] font-medium mt-1">
                          {language === 'ru' ? 'Новый лид от Анель' : 'New Anel lead'}{p.pleep_status ? ` · ${p.pleep_status}` : ''}
                        </p>
                        {p.phone && <p className="text-xs text-[#061d36]/50 mt-0.5">{p.phone}{p.city ? ` · ${p.city}` : ''}</p>}
                      </button>
                    ))}
                    {reminders.length > 0 && reminders.map((r, i) => (
                      <div key={`rem-${i}`} className="px-4 py-3 border-b border-[#061d36]/5 bg-[#8b4444]/5">
                        <div className="flex items-center gap-2">
                          <Bell className="w-3.5 h-3.5 text-[#8b4444] flex-shrink-0" />
                          <span className="font-semibold text-sm text-[#061d36]">{r.client_name || r.name || r.title}</span>
                        </div>
                        <p className="text-xs text-[#8b4444] font-medium mt-1">
                          {r.type === 'task' ? (language === 'ru' ? 'Напоминание по задаче' : 'Task reminder') : (language === 'ru' ? 'Напоминание по лиду' : 'Lead reminder')}
                        </p>
                        {r.phone && <p className="text-xs text-[#061d36]/50 mt-0.5">{r.phone}</p>}
                      </div>
                    ))}
                    {notifications.length === 0 && reminders.length === 0 && pleepLeads.length === 0 && assignedLeads.length === 0 ? (
                      <p className="text-center text-[#061d36]/30 text-sm py-6">{language === 'en' ? 'No notifications' : 'Нет уведомлений'}</p>
                    ) : notifications.map((n, i) => (
                      <div key={i} className={`px-4 py-3 border-b border-[#061d36]/5 ${!n.read ? 'bg-[#8b4444]/5' : ''}`}>
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${!n.read ? 'bg-[#8b4444]' : 'bg-[#061d36]/20'}`} />
                          <span className="font-semibold text-sm text-[#061d36]">#{n.order_name}</span>
                          <span className="ml-auto text-xs text-[#061d36]/40">{n.sum > 0 ? `${Math.round(n.sum).toLocaleString()} ₸` : ''}</span>
                        </div>
                        <p className="text-xs text-[#8b4444] font-medium mt-1">{language === 'en' ? 'Ready to ship!' : 'Готов к отгрузке!'}</p>
                        {n.agent && <p className="text-xs text-[#061d36]/40 mt-0.5">{n.agent}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <a href="/" target="_blank" rel="noreferrer"
              className="flex items-center gap-1.5 text-white/40 hover:text-white text-sm transition-colors border border-white/10 px-3 py-1.5 rounded-lg hover:border-white/30"
              data-testid="dealer-go-to-site">
              <ExternalLink className="w-3.5 h-3.5" />{t.goToSite}
            </a>
            <ChangeMyPasswordButton token={token} />
            <button onClick={logout} className="flex items-center gap-2 text-white/50 hover:text-white text-sm transition-colors" data-testid="dealer-logout-btn">
              <LogOut className="w-4 h-4" />{t.logout}
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-[#061d36]/10 sticky top-0 z-30">
        <div className="container mx-auto px-4">
          <div className="flex gap-1 overflow-x-auto py-2 scrollbar-hide" style={{scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch'}}>
            {allTabs.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.key;
              return (
                <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${isActive ? 'bg-[#8b4444] text-white' : 'text-[#061d36]/60 hover:bg-[#061d36]/5'}`}
                  data-testid={`dealer-tab-${tab.key}`}>
                  <Icon className="w-4 h-4" />
                  {t.tabs[tab.key]}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Notification banner */}
      {showBanner && unreadCount > 0 && (
        <div className="bg-[#8b4444] text-white" data-testid="notif-banner">
          <div className="container mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Bell className="w-5 h-5 animate-bounce" />
              <span className="text-sm font-medium">
                {unreadCount} {unreadCount === 1 ? (language === 'en' ? 'order ready to ship!' : 'заказ готов к отгрузке!') : (language === 'en' ? 'orders ready to ship!' : 'заказов готовы к отгрузке!')}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => { setActiveTab('orders'); setShowBanner(false); }}
                className="px-3 py-1 bg-white/20 hover:bg-white/30 rounded-lg text-xs font-medium transition-colors">
                {language === 'en' ? 'View orders' : 'Перейти к заказам'}
              </button>
              <button onClick={() => setShowBanner(false)} className="p-1 hover:bg-white/20 rounded-lg transition-colors"><X className="w-4 h-4" /></button>
            </div>
          </div>
        </div>
      )}

      {/* Content — constructor renders full-bleed without container wrapper */}
      {activeTab === 'constructor' ? (
        <InteriorConstructor
          catalogData={{
            standard: textureCollections.standard.textures,
            cashmere: textureCollections.cashmere.textures,
            premium: textureCollections.premium.textures,
            superPremium: textureCollections.superPremium.textures,
            luxury: textureCollections.luxury.textures,
          }}
          exclusiveCollections={{
            oriental: { textures: textureCollections.oriental.textures },
            grey: { textures: textureCollections.grey.textures },
            nomad: textureCollections.nomad,
          }}
          catalogImages={constructorImages}
        />
      ) : (
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {activeTab === 'crm' && <CRMPanel token={token} userRole={dealer?.role} />}
        {activeTab === 'tasks' && <TaskBoard token={token} userRole={dealer?.role} userLogin={dealer?.login} />}
        {activeTab === 'objects' && <ObjectsMap token={token} userRole={dealer?.role} />}
        {activeTab === 'catalog' && <CatalogTab token={token} />}
        {activeTab === 'stock' && <StockTab token={token} isAgent={isAgent} isAdmin={isAdmin} hidePrices={hidePrices} />}
        {activeTab === 'orders' && <OrdersTab token={token} isAdmin={isAdmin} />}
        {activeTab === 'prices' && <PricesTab />}
        {activeTab === 'calculator' && <PanelCalculator />}
        {activeTab === 'gallery' && (
          <MyVisualizationsGallery
            token={token}
            userRole={dealer?.role}
            onNavigateToConstructor={() => setActiveTab('constructor')}
          />
        )}
        {activeTab === 'script' && (dealer?.role === 'admin' || dealer?.role === 'manager') && (
          <div className="bg-white rounded-2xl shadow-sm border border-[#061d36]/10 overflow-hidden max-w-full" data-testid="sales-script-container">
            <div className="px-6 py-4 border-b border-[#061d36]/10 flex items-center gap-3">
              <BookOpen className="w-5 h-5 text-[#8b4444]" />
              <h2 className="text-lg font-semibold text-[#061d36]">{t.tabs.script}</h2>
            </div>
            <iframe
              src={`${API_URL}/api/static/script_sales.html`}
              title="Sales Script"
              className="w-full block"
              style={{ height: 'calc(100vh - 220px)', minHeight: '600px', border: 'none' }}
              data-testid="sales-script-iframe"
            />
          </div>
        )}
        {activeTab === 'trainer' && (dealer?.role === 'admin' || dealer?.role === 'manager') && (
          <div className="bg-white rounded-2xl shadow-sm border border-[#061d36]/10 overflow-hidden max-w-full" data-testid="sales-trainer-container">
            <div className="px-6 py-4 border-b border-[#061d36]/10 flex items-center gap-3">
              <Gamepad2 className="w-5 h-5 text-[#8b4444]" />
              <h2 className="text-lg font-semibold text-[#061d36]">{t.tabs.trainer}</h2>
            </div>
            <iframe
              src={`${API_URL}/api/static/trainer_sales.html`}
              title="Sales Trainer"
              className="w-full block"
              style={{ height: 'calc(100vh - 220px)', minHeight: '600px', border: 'none' }}
              data-testid="sales-trainer-iframe"
            />
          </div>
        )}
        {activeTab === 'ai_stats' && isAdmin && <AIStatsTab token={token} />}
        {activeTab === 'staff' && dealer?.login === 'admin' && <StaffTab token={token} />}
        {activeTab === 'docs' && <DocsTab />}
      </div>
      )}

      {/* WhatsApp Sales */}
      <a href="https://wa.me/77472755088?text=%D0%97%D0%B4%D1%80%D0%B0%D0%B2%D1%81%D1%82%D0%B2%D1%83%D0%B9%D1%82%D0%B5%21"
        target="_blank" rel="noopener noreferrer"
        className="fixed bottom-4 right-4 z-40 flex items-center gap-2 bg-[#25D366] hover:bg-[#20BD5A] text-white pl-4 pr-5 py-3 rounded-full shadow-lg hover:shadow-xl transition-all group"
        data-testid="dealer-whatsapp-btn">
        <svg className="w-6 h-6 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
        <span className="text-sm font-medium hidden sm:inline">Айлана</span>
        <span className="absolute inset-0 rounded-full bg-[#25D366] opacity-20 pointer-events-none"></span>
      </a>
    </div>
  );
}
